import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useEffect, createContext, useMemo, useRef, useContext } from "react";
import { createPortal } from "react-dom";
const ManageConsent = ({
  theme = "light",
  tFunction,
  onSave,
  onCancel,
  initialPreferences = {
    Analytics: false,
    Social: false,
    Advertising: false
  },
  detailedConsent
}) => {
  const [consent, setConsent] = useState(initialPreferences);
  const handleToggle = (category) => {
    setConsent((prev) => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  const handleSave = () => {
    onSave(consent);
  };
  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString(void 0, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch (e) {
      return "Invalid date";
    }
  };
  const renderConsentStatus = (category) => {
    if (!detailedConsent || !detailedConsent[category]) return null;
    const status = detailedConsent[category];
    return /* @__PURE__ */ jsx(
      "p",
      {
        className: `text-xs mt-1 text-left ${theme === "light" ? "text-gray-500" : "text-gray-500"}`,
        children: tFunction("manageCookiesStatus", {
          status: status.consented ? tFunction("manageCookiesStatusConsented") : tFunction("manageCookiesStatusDeclined"),
          date: formatDate(status.timestamp)
        })
      }
    );
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-6", children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(
        "h3",
        {
          className: `text-sm font-semibold mb-2 ${theme === "light" ? "text-gray-900" : "text-white"}`,
          children: tFunction("manageTitle")
        }
      ),
      /* @__PURE__ */ jsx(
        "p",
        {
          className: `text-xs ${theme === "light" ? "text-gray-700" : "text-gray-200"}`,
          children: tFunction("manageMessage")
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between", children: [
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx(
            "h4",
            {
              className: `text-xs font-medium text-left ${theme === "light" ? "text-gray-900" : "text-white"}`,
              children: tFunction("manageEssentialTitle")
            }
          ),
          /* @__PURE__ */ jsx(
            "p",
            {
              className: `text-xs text-left ${theme === "light" ? "text-gray-600" : "text-gray-400"}`,
              children: tFunction("manageEssentialSubtitle")
            }
          ),
          /* @__PURE__ */ jsx(
            "p",
            {
              className: `text-xs mt-1 text-left ${theme === "light" ? "text-gray-500" : "text-gray-500"}`,
              children: tFunction("manageEssentialStatus")
            }
          )
        ] }),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: `px-3 py-1 text-xs text-center font-medium rounded-full ${theme === "light" ? "bg-gray-200 text-gray-600" : "bg-gray-800 text-gray-300"}`,
            children: tFunction("manageEssentialStatusButtonText")
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between", children: [
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx(
            "h4",
            {
              className: `text-xs font-medium text-left ${theme === "light" ? "text-gray-900" : "text-white"}`,
              children: tFunction("manageAnalyticsTitle")
            }
          ),
          /* @__PURE__ */ jsx(
            "p",
            {
              className: `text-xs text-left ${theme === "light" ? "text-gray-600" : "text-gray-400"}`,
              children: tFunction("manageAnalyticsSubtitle")
            }
          ),
          renderConsentStatus("Analytics")
        ] }),
        /* @__PURE__ */ jsxs("label", { className: "relative inline-flex items-center cursor-pointer", children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "checkbox",
              checked: consent.Analytics,
              onChange: () => handleToggle("Analytics"),
              className: "sr-only peer"
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: `w-11 h-6 rounded-full peer peer-focus:ring-2 peer-focus:ring-blue-500 
              ${theme === "light" ? "bg-gray-200 peer-checked:bg-blue-500" : "bg-gray-700 peer-checked:bg-blue-500"} 
              peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 
              after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 
              after:transition-all`
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between", children: [
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx(
            "h4",
            {
              className: `text-xs font-medium text-left ${theme === "light" ? "text-gray-900" : "text-white"}`,
              children: tFunction("manageSocialTitle")
            }
          ),
          /* @__PURE__ */ jsx(
            "p",
            {
              className: `text-xs text-left ${theme === "light" ? "text-gray-600" : "text-gray-400"}`,
              children: tFunction("manageSocialSubtitle")
            }
          ),
          renderConsentStatus("Social")
        ] }),
        /* @__PURE__ */ jsxs("label", { className: "relative inline-flex items-center cursor-pointer", children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "checkbox",
              checked: consent.Social,
              onChange: () => handleToggle("Social"),
              className: "sr-only peer"
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: `w-11 h-6 rounded-full peer peer-focus:ring-2 peer-focus:ring-blue-500 
              ${theme === "light" ? "bg-gray-200 peer-checked:bg-blue-500" : "bg-gray-700 peer-checked:bg-blue-500"} 
              peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 
              after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 
              after:transition-all`
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between", children: [
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx(
            "h4",
            {
              className: `text-xs font-medium text-left ${theme === "light" ? "text-gray-900" : "text-white"}`,
              children: tFunction("manageAdvertTitle")
            }
          ),
          /* @__PURE__ */ jsx(
            "p",
            {
              className: `text-xs text-left ${theme === "light" ? "text-gray-600" : "text-gray-400"}`,
              children: tFunction("manageAdvertSubtitle")
            }
          ),
          renderConsentStatus("Advertising")
        ] }),
        /* @__PURE__ */ jsxs("label", { className: "relative inline-flex items-center cursor-pointer", children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "checkbox",
              checked: consent.Advertising,
              onChange: () => handleToggle("Advertising"),
              className: "sr-only peer"
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: `w-11 h-6 rounded-full peer peer-focus:ring-2 peer-focus:ring-blue-500 
              ${theme === "light" ? "bg-gray-200 peer-checked:bg-blue-500" : "bg-gray-700 peer-checked:bg-blue-500"} 
              peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 
              after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 
              after:transition-all`
            }
          )
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col sm:flex-row gap-3 mt-2", children: [
      onCancel && /* @__PURE__ */ jsx(
        "button",
        {
          onClick: onCancel,
          className: `w-full sm:flex-1 px-3 py-2 sm:py-1.5 text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 hover:scale-105 ${theme === "light" ? "bg-gray-200 hover:bg-gray-300 text-gray-800" : "bg-gray-800 hover:bg-gray-700 text-gray-300"}`,
          children: tFunction("manageCancelButtonText")
        }
      ),
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: handleSave,
          className: "w-full sm:flex-1 px-3 py-2 sm:py-1.5 text-xs font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 hover:scale-105",
          children: tFunction("manageSaveButtonText")
        }
      )
    ] })
  ] });
};
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);
  return isMobile;
};
const MobileModal = ({
  showManageButton,
  privacyPolicyUrl,
  theme,
  tFunction,
  handleAccept,
  handleDecline,
  handleManage,
  isExiting,
  isEntering,
  isManaging,
  handleSavePreferences,
  handleCancelManage,
  displayType = "banner",
  initialPreferences,
  detailedConsent
}) => {
  const title = tFunction("title");
  return /* @__PURE__ */ jsxs("div", { className: "cookie-manager", children: [
    displayType === "modal" && /* @__PURE__ */ jsx("div", { className: "fixed inset-0 z-[99999] bg-black/40 backdrop-blur-sm" }),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: `
        fixed inset-x-0 bottom-0 px-4 pb-4 pt-2 z-[99999]
        transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]
        ${isExiting ? "translate-y-full" : isEntering ? "translate-y-full" : "translate-y-0"}
      `,
        children: /* @__PURE__ */ jsx(
          "div",
          {
            className: `
            p-4 mx-auto max-w-[calc(100vw-32px)]
            ${theme === "light" ? "bg-white/95 ring-1 ring-black/10" : "bg-black/95 ring-1 ring-white/10"}
            rounded-2xl backdrop-blur-sm backdrop-saturate-150
          `,
            children: isManaging ? /* @__PURE__ */ jsx(
              ManageConsent,
              {
                theme,
                tFunction,
                onSave: handleSavePreferences,
                onCancel: handleCancelManage,
                initialPreferences,
                detailedConsent
              }
            ) : /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
              title && /* @__PURE__ */ jsx(
                "h3",
                {
                  className: `font-semibold my-0 ${theme === "light" ? "text-gray-900" : "text-white"}`,
                  children: title
                }
              ),
              /* @__PURE__ */ jsx(
                "p",
                {
                  className: `text-sm ${theme === "light" ? "text-gray-700" : "text-gray-200"}`,
                  children: tFunction("message")
                }
              ),
              /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    onClick: handleAccept,
                    className: "w-full px-3 py-2.5 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 focus-visible:outline-none focus:outline-none focus-visible:outline-transparent focus:outline-transparent",
                    children: tFunction("buttonText")
                  }
                ),
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    onClick: handleDecline,
                    className: `w-full px-3 py-2.5 text-sm font-medium rounded-lg focus-visible:outline-none focus:outline-none focus-visible:outline-transparent focus:outline-transparent
                    ${theme === "light" ? "bg-gray-200 hover:bg-gray-300 text-gray-800" : "bg-gray-800 hover:bg-gray-700 text-gray-300"}`,
                    children: tFunction("declineButtonText")
                  }
                ),
                showManageButton && /* @__PURE__ */ jsx(
                  "button",
                  {
                    onClick: handleManage,
                    className: "w-full px-3 py-2.5 text-sm font-medium bg-transparent text-blue-500 border border-blue-500 rounded-lg hover:text-blue-400 hover:border-blue-400 focus-visible:outline-none focus:outline-none focus-visible:outline-transparent focus:outline-transparent",
                    children: tFunction("manageButtonText")
                  }
                )
              ] }),
              privacyPolicyUrl && /* @__PURE__ */ jsx(
                "a",
                {
                  href: privacyPolicyUrl,
                  target: "_blank",
                  rel: "noopener noreferrer",
                  className: `text-xs ${theme === "light" ? "text-gray-500 hover:text-gray-700" : "text-gray-400 hover:text-gray-200"}`,
                  children: tFunction("privacyPolicyText")
                }
              )
            ] })
          }
        )
      }
    )
  ] });
};
const CookieConsenter = ({
  showManageButton = false,
  privacyPolicyUrl,
  displayType = "banner",
  theme = "light",
  tFunction,
  onAccept,
  onDecline,
  onManage,
  initialPreferences = {
    Analytics: false,
    Social: false,
    Advertising: false
  },
  detailedConsent,
  isManaging = false
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const [isEntering, setIsEntering] = useState(true);
  const [shouldRender, setShouldRender] = useState(true);
  const isMobile = useIsMobile();
  useEffect(() => {
    setTimeout(() => {
      setIsEntering(false);
    }, 50);
  }, []);
  useEffect(() => {
    if (isExiting) {
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isExiting]);
  const handleAcceptClick = (e) => {
    e.preventDefault();
    setIsExiting(true);
    setTimeout(() => {
      if (onAccept) onAccept();
    }, 500);
  };
  const handleDeclineClick = (e) => {
    e.preventDefault();
    setIsExiting(true);
    setTimeout(() => {
      if (onDecline) onDecline();
    }, 500);
  };
  const handleManageClick = (e) => {
    e.preventDefault();
    if (onManage) onManage();
  };
  const handleSavePreferences = (categories) => {
    setIsExiting(true);
    setTimeout(() => {
      if (onManage) {
        onManage(categories);
      }
    }, 500);
  };
  const handleCancelManage = () => {
    setIsExiting(true);
    setTimeout(() => {
      if (onManage) onManage();
    }, 500);
  };
  if (!shouldRender) return null;
  if (isManaging) {
    return null;
  }
  if (isMobile) {
    return createPortal(
      /* @__PURE__ */ jsx(
        MobileModal,
        {
          ...{
            showManageButton,
            privacyPolicyUrl,
            theme,
            tFunction,
            handleAccept: handleAcceptClick,
            handleDecline: handleDeclineClick,
            handleManage: handleManageClick,
            isExiting,
            isEntering,
            isManaging: false,
            handleSavePreferences,
            handleCancelManage,
            displayType,
            initialPreferences,
            detailedConsent
          }
        }
      ),
      document.body
    );
  }
  const modalBaseClasses = `
    fixed inset-0 flex items-center justify-center p-4
    ${theme === "light" ? "bg-black/20 backdrop-blur-sm" : "bg-black/40 backdrop-blur-sm"}
    transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]
    z-[99999]
    ${isExiting ? "opacity-0" : isEntering ? "opacity-0" : "opacity-100"}
  `;
  const modalContentClasses = `
    w-full max-w-lg rounded-xl p-6
    ${theme === "light" ? "bg-white/95 ring-2 ring-gray-200" : "bg-black/95 ring-1 ring-white/10"}
    ${isExiting ? "scale-95" : isEntering ? "scale-95" : "scale-100"}
    transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]
  `;
  const modalTitleClasses = `
    text-lg font-semibold mb-3
    ${theme === "light" ? "text-gray-900" : "text-white"}
  `;
  const modalMessageClasses = `
    text-sm font-medium mb-6
    ${theme === "light" ? "text-gray-700" : "text-gray-200"}
  `;
  const popupBaseClasses = `
    fixed bottom-4 left-4 w-80
    ${theme === "light" ? "bg-white/95 ring-1 ring-black/10 shadow-lg" : "bg-black/95 ring-1 ring-white/10"}
    rounded-lg backdrop-blur-sm backdrop-saturate-150 
    transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]
    z-[99999] hover:-translate-y-2
    ${isExiting ? "opacity-0 scale-95" : isEntering ? "opacity-0 scale-95" : "opacity-100 scale-100"}
  `;
  const bannerBaseClasses = `
    fixed bottom-4 left-1/2 -translate-x-1/2 w-full md:max-w-2xl
    ${theme === "light" ? "bg-white/95 border border-black/10 shadow-lg" : "bg-black/95 ring-1 ring-white/10"}
    rounded-lg backdrop-blur-sm backdrop-saturate-150 
    transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]
    z-[99999] hover:-translate-y-2
    ${isExiting ? "opacity-0 transform translate-y-full" : isEntering ? "opacity-0 transform translate-y-full" : "opacity-100 transform translate-y-0"}
  `;
  const bannerContentClasses = `
    flex flex-col gap-4 p-4
    ${theme === "light" ? "text-gray-600" : "text-gray-300"}
  `;
  const popupContentClasses = `
    flex flex-col items-start gap-4 p-4
    ${theme === "light" ? "text-gray-600" : "text-gray-300"}
  `;
  const bannerTitleClasses = `
    text-sm font-semibold mb-1
    ${theme === "light" ? "text-gray-900" : "text-white"}
  `;
  const popupTitleClasses = `
    text-sm font-semibold mb-2
    ${theme === "light" ? "text-gray-900" : "text-white"}
  `;
  const bannerMessageClasses = `
    text-xs sm:text-sm font-medium text-center sm:text-left
    ${theme === "light" ? "text-gray-700" : "text-gray-200"}
  `;
  const popupMessageClasses = `
    text-xs font-medium
    ${theme === "light" ? "text-gray-700" : "text-gray-200"}
  `;
  const acceptButtonClasses = `
    px-3 py-1.5 text-xs font-medium rounded-md
    bg-blue-500 hover:bg-blue-600 text-white
    transition-all duration-200
    hover:scale-105 focus-visible:outline-none focus:outline-none
    focus-visible:outline-transparent focus:outline-transparent
    ${displayType === "popup" ? "flex-1" : ""}
  `;
  const declineButtonClasses = `
    px-3 py-1.5 text-xs font-medium rounded-md
    ${theme === "light" ? "bg-gray-200 hover:bg-gray-300 text-gray-800" : "bg-gray-800 hover:bg-gray-700 text-gray-300"}
    transition-all duration-200
    hover:scale-105 focus-visible:outline-none focus:outline-none
    focus-visible:outline-transparent focus:outline-transparent
    ${displayType === "popup" ? "flex-1" : ""}
  `;
  const manageButtonClasses = `
    px-3 py-1.5 text-xs font-medium rounded-md
    border border-blue-500 text-blue-500
    bg-transparent
    hover:text-blue-600 hover:border-blue-600
    transition-all duration-200
    hover:scale-105 focus-visible:outline-none focus:outline-none
    focus-visible:outline-transparent focus:outline-transparent
  `;
  const privacyLinkClasses = `
    text-xs font-medium
    ${theme === "light" ? "text-gray-500 hover:text-gray-700" : "text-gray-400 hover:text-gray-200"}
    transition-colors duration-200
  `;
  const getBaseClasses = () => {
    switch (displayType) {
      case "modal":
        return modalBaseClasses;
      case "popup":
        return popupBaseClasses;
      default:
        return bannerBaseClasses;
    }
  };
  const getContentClasses = () => {
    switch (displayType) {
      case "modal":
        return modalContentClasses;
      case "popup":
        return popupContentClasses;
      default:
        return bannerContentClasses;
    }
  };
  const getTitleClasses = () => {
    switch (displayType) {
      case "modal":
        return modalTitleClasses;
      case "popup":
        return popupTitleClasses;
      default:
        return bannerTitleClasses;
    }
  };
  const getMessageClasses = () => {
    switch (displayType) {
      case "modal":
        return modalMessageClasses;
      case "popup":
        return popupMessageClasses;
      default:
        return bannerMessageClasses;
    }
  };
  const renderContent = () => {
    const title = tFunction("title");
    if (displayType === "banner") {
      return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
        /* @__PURE__ */ jsxs("div", { children: [
          title && /* @__PURE__ */ jsx("p", { className: getTitleClasses().trim(), children: title }),
          /* @__PURE__ */ jsx("p", { className: getMessageClasses().trim(), children: tFunction("message") })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between w-full", children: [
          privacyPolicyUrl && /* @__PURE__ */ jsx(
            "a",
            {
              href: privacyPolicyUrl,
              target: "_blank",
              rel: "noopener noreferrer",
              className: privacyLinkClasses.trim(),
              children: tFunction("privacyPolicyText")
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
            showManageButton && /* @__PURE__ */ jsx(
              "button",
              {
                onClick: handleManageClick,
                className: manageButtonClasses.trim(),
                children: tFunction("manageButtonText")
              }
            ),
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: handleDeclineClick,
                className: declineButtonClasses.trim(),
                children: tFunction("declineButtonText")
              }
            ),
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: handleAcceptClick,
                className: acceptButtonClasses.trim(),
                children: tFunction("buttonText")
              }
            )
          ] })
        ] })
      ] });
    }
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
      title && /* @__PURE__ */ jsx("p", { className: getTitleClasses().trim(), children: title }),
      /* @__PURE__ */ jsx("p", { className: getMessageClasses().trim(), children: tFunction("message") })
    ] });
  };
  const renderButtons = () => {
    if (displayType === "popup") {
      return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3 w-full", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: handleDeclineClick,
              className: declineButtonClasses.trim(),
              children: tFunction("declineButtonText")
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: handleAcceptClick,
              className: acceptButtonClasses.trim(),
              children: tFunction("buttonText")
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2 w-full", children: [
          showManageButton && /* @__PURE__ */ jsx(
            "button",
            {
              onClick: handleManageClick,
              className: `${manageButtonClasses.trim()} w-full justify-center`,
              children: tFunction("manageButtonText")
            }
          ),
          privacyPolicyUrl && /* @__PURE__ */ jsx(
            "a",
            {
              href: privacyPolicyUrl,
              target: "_blank",
              rel: "noopener noreferrer",
              className: privacyLinkClasses.trim(),
              children: tFunction("privacyPolicyText")
            }
          )
        ] })
      ] });
    }
    if (displayType === "modal") {
      return /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-3", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
        privacyPolicyUrl && /* @__PURE__ */ jsx(
          "a",
          {
            href: privacyPolicyUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            className: privacyLinkClasses.trim(),
            children: tFunction("privacyPolicyText")
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
          showManageButton && /* @__PURE__ */ jsx(
            "button",
            {
              onClick: handleManageClick,
              className: manageButtonClasses.trim(),
              children: tFunction("manageButtonText")
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: handleDeclineClick,
              className: declineButtonClasses.trim(),
              children: tFunction("declineButtonText")
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: handleAcceptClick,
              className: acceptButtonClasses.trim(),
              children: tFunction("buttonText")
            }
          )
        ] })
      ] }) });
    }
    return null;
  };
  const content = /* @__PURE__ */ jsx("div", { className: "cookie-manager", children: /* @__PURE__ */ jsx("div", { className: getBaseClasses().trim(), children: displayType === "modal" ? /* @__PURE__ */ jsxs("div", { className: getContentClasses().trim(), children: [
    renderContent(),
    renderButtons()
  ] }) : /* @__PURE__ */ jsxs("div", { className: getContentClasses().trim(), children: [
    renderContent(),
    renderButtons()
  ] }) }) });
  return createPortal(content, document.body);
};
const trackers = {
  categories: {
    Analytics: [
      "63squares.com",
      "abtasty.com",
      "3dstats.com",
      "addfreestats.com",
      "adobedc.net",
      "livefyre.com",
      "mktoresp.com",
      "sitestat.com",
      "bluecava.com",
      "advombat.ru",
      "aidata.me",
      "abmr.net",
      "akstat.io",
      "go-mpulse.net",
      "pxlclnmdecom-a.akamaihd.net",
      "websitealive.com",
      "websitealive0.com",
      "websitealive1.com",
      "websitealive2.com",
      "websitealive3.com",
      "websitealive4.com",
      "websitealive5.com",
      "websitealive6.com",
      "websitealive7.com",
      "websitealive8.com",
      "websitealive9.com",
      "amazingcounters.com",
      "alexa.com",
      "alexametrics.com",
      "ttvnw.net",
      "amocrm.ru",
      "amplitude.com",
      "anormal-media.de",
      "anormal-tracker.de",
      "atoomic.com",
      "attracta.com",
      "audioeye.com",
      "aamapiv2.com",
      "clicktracks.com",
      "lyris.com",
      "intensedebate.com",
      "polldaddy.com",
      "w3roi.com",
      "b3mxnuvcer.com",
      "baidu.com",
      "bdstatic.com",
      "tns-cs.net",
      "tnsglobal.com",
      "curalate.com",
      "belstat.be",
      "belstat.com",
      "belstat.de",
      "belstat.fr",
      "belstat.nl",
      "betssonpalantir.com",
      "bitrix.info",
      "blogcounter.de",
      "blueconic.net",
      "bluemetrix.com",
      "bmmetrix.com",
      "btttag.com",
      "compuware.com",
      "ml314.com",
      "branica.com",
      "bubblestat.com",
      "bufferapp.com",
      "bunchball.com",
      "buysafe.com",
      "cbox.ws",
      "cbsinteractive.com",
      "com.com",
      "cedexis.com",
      "cedexis.net",
      "chartbeat.com",
      "chartbeat.net",
      "smartlook.com",
      "clickdensity.com",
      "clixmetrix.com",
      "clixpy.com",
      "cloudflareinsights.com",
      "clustrmaps.com",
      "cnzz.com",
      "collarity.com",
      "collective.com",
      "certifica.com",
      "comscore.com",
      "mdotlabs.com",
      "scorecardresearch.com",
      "voicefive.com",
      "conduit-banners.com",
      "conduit-services.com",
      "conduit.com",
      "wibiya.com",
      "clicktale.com",
      "clicktale.net",
      "contentsquare.net",
      "pantherssl.com",
      "convertexperiments.com",
      "convert.com",
      "reedge.com",
      "conviva.com",
      "cetrk.com",
      "crazyegg.com",
      "crowdscience.com",
      "betrad.com",
      "cuebiq.com",
      "browser-intake-datadoghq.com",
      "datadoghq-browser-agent.com",
      "datadoghq.com",
      "datadoghq.eu",
      "collserve.com",
      "dataium.com",
      "leadfeeder.com",
      "company-target.com",
      "demandbase.com",
      "lightboxcdn.com",
      "ipcounter.de",
      "distiltag.com",
      "trackedweb.net",
      "doubleverify.com",
      "drift.com",
      "driftt.com",
      "dtscdn.com",
      "dwstat.cn",
      "researchnow.com",
      "valuedopinions.co.uk",
      "axf8.net",
      "dynatrace.com",
      "gomez.com",
      "webclicktracker.com",
      "clickability.com",
      "limelight.com",
      "llnwd.net",
      "do-not-tracker.org",
      "eviltracker.net",
      "trackersimulator.org",
      "eproof.com",
      "etracker.com",
      "etracker.de",
      "sedotracker.com",
      "sedotracker.de",
      "eulerian.com",
      "eulerian.net",
      "extreme-dm.com",
      "extremetracking.com",
      "eyeota.net",
      "feedjit.com",
      "fpnpmcdn.net",
      "openfpcdn.io",
      "flattr.com",
      "footprintlive.com",
      "yabidos.com",
      "freeonlineusers.com",
      "free-pagerank.com",
      "antifraudjs.friends2follow.com",
      "fullstory.com",
      "sentry-cdn.com",
      "sentry.io",
      "fndrsp.net",
      "audienceinsights.net",
      "geetest.com",
      "boldchat.com",
      "getsatisfaction.com",
      "getsitecontrol.com",
      "daphnecm.com",
      "gfk.com",
      "gfkdaphne.com",
      "sessioncam.com",
      "trafficfacts.com",
      "analytics.google.com",
      "crashlytics.com",
      "google-analytics.com",
      "googleoptimize.com",
      "merchant-center-analytics.goog",
      "postrank.com",
      "gorgias.chat",
      "gosquared.com",
      "gostats.com",
      "graphenedigitalanalytics.in",
      "gtop.ro",
      "gtopstats.com",
      "haloscan.com",
      "heapanalytics.com",
      "raasnet.com",
      "helpscout.net",
      "histats.com",
      "hitslink.com",
      "hitsniffer.com",
      "hotjar.com",
      "hotjar.io",
      "hs-analytics.net",
      "hsforms.net",
      "hubspot.com",
      "usemessages.com",
      "adxyield.com",
      "adzmath.com",
      "clean.gg",
      "whiteops.com",
      "cmcore.com",
      "coremetrics.com",
      "ibm.com",
      "ipinfo.io",
      "infernotions.com",
      "infonline.de",
      "ioam.de",
      "iocnt.net",
      "ivwbox.de",
      "hotlog.ru",
      "infostars.ru",
      "inrix.com",
      "inspectlet.com",
      "domodomain.com",
      "onthe.io",
      "ipify.org",
      "ipqualityscore.com",
      "ipregistry.co",
      "islay.tech",
      "itisatracker.com",
      "jivosite.com",
      "js-kit.com",
      "judge.me",
      "jwpltx.com",
      "longtailvideo.com",
      "ltassrv.com",
      "keymetric.net",
      "linezing.com",
      "contactatonce.com",
      "liveperson.com",
      "liveperson.net",
      "lpsnmedia.net",
      "nuconomy.com",
      "logdy.com",
      "luckyorange.com",
      "luckyorange.net",
      "lynchpin.com",
      "lypn.com",
      "webtrekk.com",
      "webtrekk.net",
      "wsod.com",
      "9c9media.ca",
      "markmonitor.com",
      "marktest.com",
      "marktest.pt",
      "piwik.org",
      "geoip-js.com",
      "maxmind.com",
      "mmapiws.com",
      "scanalert.com",
      "kampyle.com",
      "medallia.com",
      "estat.com",
      "y-track.com",
      "sesamestats.com",
      "tns-counter.ru",
      "datasift.com",
      "tweetmeme.com",
      "merkleinc.com",
      "clarity.ms",
      "footprintdns.com",
      "gaug.es",
      "inq.com",
      "live.net",
      "microsoftstore.com",
      "officelive.com",
      "touchcommerce.com",
      "visualstudio.com",
      "mixpanel.com",
      "mxpnl.com",
      "mongoosemetrics.com",
      "monitus.net",
      "monsido.com",
      "mouseflow.com",
      "mparticle.com",
      "mycounter.com.ua",
      "myfonts.net",
      "mypagerank.net",
      "narrative.io",
      "newsinc.com",
      "ubermedia.com",
      "nedstatbasic.net",
      "hitsprocessor.com",
      "netapplications.com",
      "newrelic.com",
      "nr-data.net",
      "apnewsregistry.com",
      "glanceguide.com",
      "nielsen.com",
      "nocodelytics.com",
      "nofraud.com",
      "noibu.com",
      "notolytix.com",
      "kissmetrics.com",
      "nurago.com",
      "nurago.de",
      "sensic.net",
      "observerapp.com",
      "behavioralengine.com",
      "ooyala.com",
      "openstat.ru",
      "spylog.com",
      "opentracker.net",
      "atgsvcs.com",
      "eloqua.com",
      "instantservice.com",
      "oewa.at",
      "p7cloud.net",
      "parsely.com",
      "peerius.com",
      "pendo.io",
      "persianstat.com",
      "phonalytics.com",
      "phpmyvisites.us",
      "at-o.net",
      "aticdn.net",
      "atinternet.com",
      "piano.io",
      "tinypass.com",
      "xiti.com",
      "pixanalytics.com",
      "turnto.com",
      "opolen.com.br",
      "v12data.com",
      "v12group.com",
      "blaze.com",
      "kissinsights.com",
      "qualaroo.com",
      "qualtrics.com",
      "quantummetric.com",
      "thecounter.com",
      "quintelligence.com",
      "gogrid.com",
      "radarurl.com",
      "perfdrive.com",
      "slickstream.com",
      "thirdwatch.ai",
      "ringier.cz",
      "riskified.com",
      "rollick.io",
      "getclicky.com",
      "roxr.net",
      "staticstuff.net",
      "sageanalyst.net",
      "cquotient.com",
      "evgnet.com",
      "igodigital.com",
      "salesforce.com",
      "salesforceliveagent.com",
      "sardine.ai",
      "seevolution.com",
      "svlu.net",
      "shinystat.com",
      "sift.com",
      "simpleanalyticscdn.com",
      "simply.com",
      "siteimproveanalytics.com",
      "siteimproveanalytics.io",
      "bugsnag.com",
      "snoobi.com",
      "snplow.net",
      "pingdom.net",
      "solarwinds.com",
      "speedcurve.com",
      "pdst.fm",
      "sputnik.ru",
      "stat-track.com",
      "4u.pl",
      "statcounter.com",
      "statisfy.net",
      "statsit.com",
      "usabilla.com",
      "storeland.ru",
      "stratigent.com",
      "swymrelay.com",
      "synacor.com",
      "tealiumiq.com",
      "techsolutions.com.tw",
      "tensquare.com",
      "heronpartners.com.au",
      "tracemyip.org",
      "tracify.ai",
      "roia.biz",
      "trackingsoft.com",
      "trackset.com",
      "transcend.io",
      "iesnare.com",
      "iovation.com",
      "trustarc.com",
      "truste.com",
      "segment.io",
      "umbel.com",
      "friendshipmale.com",
      "unseenreport.com",
      "formalyzer.com",
      "onestat.com",
      "uptrends.com",
      "usabilitysciences.com",
      "webiqonline.com",
      "usbrowserspeed.com",
      "nakanohito.jp",
      "verticalacuity.com",
      "vertster.com",
      "visitstreamer.com",
      "vizisense.net",
      "punchtab.com",
      "webgozar.com",
      "webgozar.ir",
      "onlinewebstats.com",
      "web-stat.com",
      "webtrackingservices.com",
      "reinvigorate.net",
      "webtrends.com",
      "webtrendslive.com",
      "amung.us",
      "visualwebsiteoptimizer.com",
      "wingify.com",
      "woopra-ns.com",
      "woopra.com",
      "compete.com",
      "dl-rms.com",
      "dlqm.net",
      "questionmarket.com",
      "safecount.net",
      "statistik-gallup.net",
      "wysistat.com",
      "xfyun.cn",
      "adap.tv",
      "analytics.yahoo.com",
      "aol.com",
      "autoblog.com",
      "convertro.com",
      "homesessive.com",
      "mandatory.com",
      "shoutcast.com",
      "yahoofs.com",
      "informer.yandex.ru",
      "yandex.ua",
      "yellowtracker.com",
      "yoox.it",
      "zdassets.com",
      "zendesk.com",
      "zopim.com",
      "zoominfo.com"
    ],
    Social: [
      "causes.com",
      "digg.com",
      "developers.google.com",
      "gmail.com",
      "googlemail.com",
      "plus.google.com",
      "plusone.google.com",
      "smartlock.google.com",
      "voice.google.com",
      "lockerz.com",
      "atdmt.com",
      "facebook.com",
      "facebook.de",
      "facebook.fr",
      "facebook.net",
      "fb.com",
      "fbsbx.com",
      "friendfeed.com",
      "licdn.com",
      "linkedin.com",
      "addthis.com",
      "addthiscdn.com",
      "addthisedge.com",
      "clearspring.com",
      "reddit.com",
      "shareaholic.com",
      "sharethis.com",
      "snapchat.com",
      "stumble-upon.com",
      "stumbleupon.com",
      "tiktok.com",
      "twimg.com",
      "twitter.com",
      "twitter.jp",
      "x.com",
      "list.ru",
      "mail.ru",
      "userapi.com",
      "vk.com",
      "vkontakte.ru",
      "avatars.yahoo.com",
      "buzz.yahoo.com",
      "calendar.yahoo.com",
      "edit.yahoo.com",
      "login.yahoo.com",
      "mail.yahoo.com",
      "my.yahoo.com",
      "mybloglog.com",
      "pulse.yahoo.com",
      "rocketmail.com",
      "webmessenger.yahoo.com",
      "ymail.com"
    ],
    ConsentManagers: [
      "cookielaw.org",
      "onetrust.com",
      "onetrust.io",
      "osano.com",
      "privacy-mgmt.com",
      "summerhamster.com",
      "cookiebot.com",
      "cookiebot.eu"
    ],
    Email: [
      "10web.io",
      "4dem.it",
      "8d8.biz",
      "open.mkt10008.com",
      "open.mkt10039.com",
      "open.mkt10049.com",
      "open.mkt10067.com",
      "open.mkt10114.com",
      "open.mkt10153.com",
      "open.mkt10663.com",
      "open.mkt10781.com",
      "open.mkt1248.com",
      "open.mkt1365.com",
      "open.mkt1937.com",
      "open.mkt1946.com",
      "open.mkt2178.com",
      "open.mkt2478.com",
      "open.mkt2685.com",
      "open.mkt2724.com",
      "open.mkt32.net",
      "open.mkt3469.com",
      "open.mkt3536.com",
      "open.mkt3797.com",
      "open.mkt3798.com",
      "open.mkt3838.com",
      "open.mkt4091.com",
      "open.mkt41.net",
      "open.mkt4158.com",
      "open.mkt4261.com",
      "open.mkt4424.com",
      "open.mkt4463.com",
      "open.mkt4477.com",
      "open.mkt4644.com",
      "open.mkt5089.com",
      "open.mkt51.net",
      "open.mkt5216.com",
      "open.mkt5224.com",
      "open.mkt5297.com",
      "open.mkt5379.com",
      "open.mkt5419.com",
      "open.mkt5514.com",
      "open.mkt5566.com",
      "open.mkt5654.com",
      "open.mkt5657.com",
      "open.mkt5906.com",
      "open.mkt6031.com",
      "open.mkt61.net",
      "open.mkt6260.com",
      "open.mkt6264.com",
      "open.mkt6288.com",
      "open.mkt6316.com",
      "open.mkt6478.com",
      "open.mkt6688.com",
      "open.mkt6735.com",
      "open.mkt6793.com",
      "open.mkt685.com",
      "open.mkt6882.com",
      "open.mkt6903.com",
      "open.mkt6917.com",
      "open.mkt6967.com",
      "open.mkt71.net",
      "open.mkt7234.com",
      "open.mkt7580.com",
      "open.mkt7596.com",
      "open.mkt7752.com",
      "open.mkt7783.com",
      "open.mkt7832.com",
      "open.mkt7842.com",
      "open.mkt7883.com",
      "open.mkt7946.com",
      "open.mkt7971.com",
      "open.mkt7972.com",
      "open.mkt7974.com",
      "open.mkt8007.com",
      "open.mkt8008.com",
      "open.mkt8043.com",
      "open.mkt8062.com",
      "open.mkt8063.com",
      "open.mkt8064.com",
      "open.mkt8096.com",
      "open.mkt81.net",
      "open.mkt8133.com",
      "open.mkt8163.com",
      "open.mkt8267.com",
      "open.mkt829.com",
      "open.mkt8345.com",
      "open.mkt8369.com",
      "open.mkt8586.com",
      "open.mkt8628.com",
      "open.mkt8756.com",
      "open.mkt8763.com",
      "open.mkt8988.com",
      "open.mkt9026.com",
      "open.mkt9054.com",
      "open.mkt912.com",
      "open.mkt9203.com",
      "open.mkt922.com",
      "open.mkt941.com",
      "open.mkt9430.com",
      "open.mkt9775.com",
      "open.mkt9862.com",
      "open.mkt9923.com",
      "open.mkt9942.com",
      "basiscommunicatie.nl",
      "acblnk.com",
      "acmbtrc.com",
      "acmtrk.com",
      "clickacumba.com",
      "emlmkt.com",
      "mailmktool.com",
      "trckacbm.com",
      "adform.net",
      "2o7.net",
      "demdex.net",
      "omtrdc.net",
      "adspeed.net",
      "adsugar.ch",
      "aforesponse.com",
      "agilemeasure.com",
      "authoremail.com",
      "apms5.com",
      "autopilotmail.io",
      "bandzoogle.com",
      "barilliance.com",
      "sitescout.com",
      "bemail.it",
      "bentonow.com",
      "cdn.uk.exponea.com",
      "sptracking.getblueshift.com",
      "cp20.com",
      "cpro20.com",
      "cpro30.com",
      "skem1.com",
      "api.carrotquest.io",
      "cdnwidget.com",
      "cl1-api.connectif.cloud",
      "eu2-api.connectif.cloud",
      "eu3-api.connectif.cloud",
      "eu4-api.connectif.cloud",
      "convertkit-mail.com",
      "convertkit-mail2.com",
      "convertkit-mail3.com",
      "convertkit-mail4.com",
      "convertkit-mail5.com",
      "convertkit-mail6.com",
      "e.customeriomail.com",
      "mailer.lassocrm.com",
      "edrone.me",
      "emaillabs.co",
      "engagingnetworks.app",
      "ot.gliq.com",
      "track.gliq.com",
      "doubleclick.net",
      "google-analytics.com",
      "gasv1.com",
      "infernotions.com",
      "email-messaging.com",
      "ixactcontact.com",
      "track-mb.bra2hmail.com",
      "liadm.com",
      "pippio.com",
      "m3651.net",
      "m3652.net",
      "mailblue.eu",
      "eds5.mailcamp.nl",
      "agentofficemail.com",
      "mailcamp.net.pl",
      "mailtracker.pl",
      "senderit.pl",
      "xylionmail.pl",
      "mathtag.com",
      "api-01.moengage.com",
      "adroll.com",
      "myvisualiq.net",
      "nyl.as",
      "openeducat.org",
      "pvd.to",
      "leadersend.com",
      "pstmrk.it",
      "pushnami.com",
      "opens.responder.co.il",
      "system.send24.pl",
      "tracking.retailrocket.net",
      "sailplay.ru",
      "krxd.net",
      "pixel.inbox.exacttarget.com",
      "ondemand.com",
      "sendcloud.net",
      "sendfox.com",
      "sendiio.app",
      "sendpul.se",
      "smlists.com",
      "smartsendy.com",
      "smtp2go.com",
      "smtp2go.net",
      "servedbyadbutler.com",
      "strikestack.com",
      "hodes.com",
      "tealiumiq.com",
      "adventure-novels.com",
      "agkn.com",
      "thebrighttag.com",
      "segment.io",
      "returnpath.net",
      "vinc.fr",
      "viralhosts.com",
      "trac.visme.co",
      "stable.cz",
      "campaign-tracking.woowup.com",
      "e.wordfly.com",
      "xpressmail.hu",
      "xtremepush.com",
      "em.yotpo.com",
      "p.yotpo.com",
      "1-2-1marketing.com",
      "accelo.com",
      "mkt10008.com",
      "mkt10039.com",
      "mkt10049.com",
      "mkt10067.com",
      "mkt10114.com",
      "mkt10153.com",
      "mkt10663.com",
      "mkt10781.com",
      "mkt1248.com",
      "mkt1365.com",
      "mkt1937.com",
      "mkt1946.com",
      "mkt2178.com",
      "mkt2478.com",
      "mkt2685.com",
      "mkt2724.com",
      "mkt32.net",
      "mkt3469.com",
      "mkt3536.com",
      "mkt3797.com",
      "mkt3798.com",
      "mkt3838.com",
      "mkt4091.com",
      "mkt41.net",
      "mkt4158.com",
      "mkt4261.com",
      "mkt4424.com",
      "mkt4463.com",
      "mkt4477.com",
      "mkt4644.com",
      "mkt5089.com",
      "mkt51.net",
      "mkt5216.com",
      "mkt5224.com",
      "mkt5297.com",
      "mkt5379.com",
      "mkt5419.com",
      "mkt5514.com",
      "mkt5566.com",
      "mkt5654.com",
      "mkt5657.com",
      "mkt5906.com",
      "mkt6031.com",
      "mkt61.net",
      "mkt6260.com",
      "mkt6264.com",
      "mkt6288.com",
      "mkt6316.com",
      "mkt6478.com",
      "mkt6688.com",
      "mkt6735.com",
      "mkt6793.com",
      "mkt685.com",
      "mkt6882.com",
      "mkt6903.com",
      "mkt6917.com",
      "mkt6967.com",
      "mkt71.net",
      "mkt7234.com",
      "mkt7580.com",
      "mkt7596.com",
      "mkt7752.com",
      "mkt7783.com",
      "mkt7832.com",
      "mkt7842.com",
      "mkt7883.com",
      "mkt7946.com",
      "mkt7971.com",
      "mkt7972.com",
      "mkt7974.com",
      "mkt8007.com",
      "mkt8008.com",
      "mkt8043.com",
      "mkt8062.com",
      "mkt8063.com",
      "mkt8064.com",
      "mkt8096.com",
      "mkt81.net",
      "mkt8133.com",
      "mkt8163.com",
      "mkt8267.com",
      "mkt829.com",
      "mkt8345.com",
      "mkt8369.com",
      "mkt8586.com",
      "mkt8628.com",
      "mkt8756.com",
      "mkt8763.com",
      "mkt8988.com",
      "mkt9026.com",
      "mkt9054.com",
      "mkt912.com",
      "mkt9203.com",
      "mkt922.com",
      "mkt941.com",
      "mkt9430.com",
      "mkt9775.com",
      "mkt9862.com",
      "mkt9923.com",
      "mkt9942.com",
      "acemlna.com",
      "acemlnb.com",
      "acemlnc.com",
      "acemlnd.com",
      "activehosted.com",
      "emlnk1.com",
      "acsmedia.us",
      "activedemand.com",
      "adobe.com",
      "affinity.co",
      "agilecrm.com",
      "aisleahead.com",
      "awstrack.me",
      "atomicpark.email",
      "autoklose.com",
      "bitrix24.com",
      "exponea.com",
      "getblueshift.com",
      "mailstat.us",
      "buttondown.email",
      "campaignmonitor.com",
      "cmail1.com",
      "cmail10.com",
      "cmail11.com",
      "cmail12.com",
      "cmail13.com",
      "cmail14.com",
      "cmail15.com",
      "cmail16.com",
      "cmail17.com",
      "cmail18.com",
      "cmail19.com",
      "cmail2.com",
      "cmail20.com",
      "cmail21.com",
      "cmail22.com",
      "cmail23.com",
      "cmail24.com",
      "cmail25.com",
      "cmail26.com",
      "cmail27.com",
      "cmail28.com",
      "cmail29.com",
      "cmail3.com",
      "cmail30.com",
      "cmail4.com",
      "cmail5.com",
      "cmail6.com",
      "cmail7.com",
      "cmail8.com",
      "cmail9.com",
      "confirmsubscription.com",
      "createsend1.com",
      "createsend10.com",
      "createsend11.com",
      "createsend12.com",
      "createsend13.com",
      "createsend14.com",
      "createsend15.com",
      "createsend16.com",
      "createsend17.com",
      "createsend18.com",
      "createsend19.com",
      "createsend2.com",
      "createsend20.com",
      "createsend21.com",
      "createsend22.com",
      "createsend23.com",
      "createsend24.com",
      "createsend25.com",
      "createsend26.com",
      "createsend27.com",
      "createsend28.com",
      "createsend29.com",
      "createsend3.com",
      "createsend30.com",
      "createsend4.com",
      "createsend5.com",
      "createsend6.com",
      "createsend7.com",
      "createsend8.com",
      "createsend9.com",
      "forwardtomyfriend.com",
      "carrotquest.io",
      "cirrusinsight.com",
      "close.io",
      "connectif.cloud",
      "constantcontact.com",
      "rs6.net",
      "contactmonkey.com",
      "copper.com",
      "customer.io",
      "customeriomail.com",
      "e-mail-servers.com",
      "lassocrm.com",
      "email-signature-image.com",
      "my-email-signature.link",
      "engagebay.com",
      "ezymarketer.net",
      "emailinc.net",
      "followup.cc",
      "frontapp.com",
      "email81.com",
      "gliq.com",
      "hsms06.com",
      "hubspotemail.net",
      "hunter.io",
      "mltrk.io",
      "intercom.io",
      "infusionsoft.com",
      "leadboxer.com",
      "bra2hmail.com",
      "emltrk.com",
      "mailbutler.io",
      "mailcamp.nl",
      "answerbook.com",
      "list-manage.com",
      "list-manage1.com",
      "mandrillapp.com",
      "tinyletter.com",
      "mailerlite.com",
      "mailtag.io",
      "mailtrack.io",
      "mixmax.com",
      "mixpanel.com",
      "moengage.com",
      "nhlnka.com",
      "outrch.com",
      "outreach.io",
      "persistiq.com",
      "polymail.io",
      "propelleremail.co.uk",
      "lahar.com.br",
      "responder.co.il",
      "send24.pl",
      "reply.io",
      "retailrocket.net",
      "revenuegrid.com",
      "rightinbox.com",
      "sailthru.com",
      "exacttarget.com",
      "exct.net",
      "pardot.com",
      "salesforceiq.com",
      "shetrk.com",
      "salesloft.com",
      "salesloftlinks.com",
      "mailfoogae.appspot.com",
      "substack.com",
      "superhuman.com",
      "tradiewebguys.com.au",
      "sendgrid.net",
      "user.com",
      "visme.co",
      "vocus.io",
      "woowup.com",
      "wordfly.com",
      "bounceexchange.com",
      "yesware.com",
      "yotpo.com",
      "zendable.com",
      "icptrack.com",
      "maillist-manage.com",
      "maillist-manage.com.au",
      "maillist-manage.eu",
      "maillist-manage.in"
    ],
    Advertising: [
      "24log.com",
      "2leep.com",
      "33across.com",
      "tynt.com",
      "360.cn",
      "51yes.com",
      "6sc.co",
      "6sense.com",
      "a-ads.com",
      "abaxinteractive.com",
      "accelia.net",
      "durasite.net",
      "adaptly.com",
      "acsbapp.com",
      "mkt51.net",
      "pages05.net",
      "silverpop.com",
      "vtrenz.net",
      "acquisio.com",
      "actisens.com",
      "gestionpub.com",
      "app-us1.com",
      "trackcmp.net",
      "activeconversion.com",
      "activemeter.com",
      "activengage.com",
      "act-on.com",
      "actonsoftware.com",
      "acuity.com",
      "acuityads.com",
      "acuityplatform.com",
      "acxiom.com",
      "acxiomapac.com",
      "ad2onegroup.com",
      "ad4game.com",
      "ad6media.fr",
      "adacado.com",
      "4dex.io",
      "adality.de",
      "adrtx.net",
      "ad-alliance.de",
      "adalliance.io",
      "technical-service.net",
      "adaptiveads.com",
      "a-mo.net",
      "a-mx.com",
      "rtb.mx",
      "adaramedia.com",
      "yieldoptimizer.com",
      "adbot.tw",
      "adcash.com",
      "ufpcdn.com",
      "a2dfp.net",
      "addecisive.com",
      "ad-delivery.net",
      "addtoany.com",
      "addvantagemedia.com",
      "addynamo.com",
      "addynamo.net",
      "adelement.com",
      "adengage.com",
      "adextent.com",
      "adfonic.com",
      "adform.com",
      "adform.net",
      "adformdsp.net",
      "seadform.net",
      "adfrontiers.com",
      "shorttailmedia.com",
      "adglare.com",
      "adglare.net",
      "adhood.com",
      "adblade.com",
      "adiant.com",
      "adimpact.com",
      "adjug.com",
      "adjuggler.com",
      "adjuggler.net",
      "adjust.com",
      "adkernel.com",
      "adknife.com",
      "adknowledge.com",
      "bidsystem.com",
      "adleave.com",
      "adloox.com",
      "adlooxtracking.com",
      "adlucent.com",
      "admagnet.com",
      "admagnet.net",
      "adman.gr",
      "admanmedia.com",
      "admarketplace.com",
      "admarketplace.net",
      "ampxchange.com",
      "admatrix.jp",
      "ad-maven.com",
      "wrethicap.info",
      "admaximizer.com",
      "admedia.com",
      "admedo.com",
      "admeta.com",
      "atemda.com",
      "2znp09oa.com",
      "4jnzhl0d0.com",
      "5mcwl.pw",
      "6ldu6qa.com",
      "82o9v830.com",
      "abilityscale.com",
      "aboardamusement.com",
      "aboardlevel.com",
      "abovechat.com",
      "abruptroad.com",
      "absentairport.com",
      "absorbingband.com",
      "absorbingprison.com",
      "abstractedamount.com",
      "abstractedauthority.com",
      "absurdapple.com",
      "abundantcoin.com",
      "accurateanimal.com",
      "accuratecoal.com",
      "achieverknee.com",
      "acidicstraw.com",
      "acridangle.com",
      "acridtwist.com",
      "actuallysheep.com",
      "actuallysnake.com",
      "actuallything.com",
      "adamantsnail.com",
      "addictedattention.com",
      "admiral.pub",
      "adorableanger.com",
      "adorableattention.com",
      "adventurousamount.com",
      "afraidlanguage.com",
      "agilebreeze.com",
      "agreeablearch.com",
      "agreeabletouch.com",
      "aheadday.com",
      "aheadmachine.com",
      "ak0gsh40.com",
      "aliasanvil.com",
      "aliveachiever.com",
      "alleythecat.com",
      "alluringbucket.com",
      "aloofmetal.com",
      "aloofvest.com",
      "alpineactor.com",
      "ambientdusk.com",
      "ambientlagoon.com",
      "ambiguousafternoon.com",
      "ambiguousanger.com",
      "ambiguousdinosaurs.com",
      "ambiguousincome.com",
      "ambrosialsummit.com",
      "amethystzenith.com",
      "amuckafternoon.com",
      "amusedbucket.com",
      "analogwonder.com",
      "analyzecorona.com",
      "ancientact.com",
      "annoyingacoustics.com",
      "annoyingclover.com",
      "anxiousapples.com",
      "aquaticowl.com",
      "ar1nvz5.com",
      "archswimming.com",
      "aromamirror.com",
      "arrivegrowth.com",
      "artthevoid.com",
      "aspiringapples.com",
      "aspiringattempt.com",
      "aspiringtoy.com",
      "astonishingfood.com",
      "astrallullaby.com",
      "attendchase.com",
      "attractionbanana.com",
      "attractivecap.com",
      "audioarctic.com",
      "automaticside.com",
      "automaticturkey.com",
      "availablerest.com",
      "avalonalbum.com",
      "averageactivity.com",
      "awarealley.com",
      "awzbijw.com",
      "axiomaticalley.com",
      "axiomaticanger.com",
      "azuremystique.com",
      "badgeboat.com",
      "badgerabbit.com",
      "bagbeam.com",
      "baitbaseball.com",
      "balloonbelieve.com",
      "ballsbanana.com",
      "bananabarrel.com",
      "bandborder.com",
      "barbarousbase.com",
      "basilfish.com",
      "basketballbelieve.com",
      "baskettexture.com",
      "bawdybalance.com",
      "bawdybeast.com",
      "beamvolcano.com",
      "beancontrol.com",
      "bedsberry.com",
      "beginnerpancake.com",
      "berserkhydrant.com",
      "bespokesandals.com",
      "bestboundary.com",
      "bewilderedbattle.com",
      "bewilderedblade.com",
      "bhcumsc.com",
      "bikepaws.com",
      "bikesboard.com",
      "billowybead.com",
      "billowybelief.com",
      "binspiredtees.com",
      "birthdaybelief.com",
      "blackbrake.com",
      "bleachbubble.com",
      "bleachscarecrow.com",
      "bleedlight.com",
      "blesspizzas.com",
      "blissfulcrescendo.com",
      "blissfullagoon.com",
      "blueeyedblow.com",
      "blushingbeast.com",
      "blushingbread.com",
      "boatsvest.com",
      "boilingbeetle.com",
      "boostbehavior.com",
      "boredcrown.com",
      "bouncyproperty.com",
      "boundarybusiness.com",
      "boundlessargument.com",
      "boundlessbrake.com",
      "boundlessveil.com",
      "brainybasin.com",
      "brainynut.com",
      "branchborder.com",
      "brandsfive.com",
      "brandybison.com",
      "bravebone.com",
      "breadbalance.com",
      "breakableinsurance.com",
      "breakfastboat.com",
      "breezygrove.com",
      "brianwould.com",
      "brighttoe.com",
      "briskstorm.com",
      "broadborder.com",
      "broadboundary.com",
      "broadcastbed.com",
      "broaddoor.com",
      "brotherslocket.com",
      "bruisebaseball.com",
      "brunchforher.com",
      "buildingknife.com",
      "bulbbait.com",
      "burgersalt.com",
      "burlywhistle.com",
      "burnbubble.com",
      "bushesbag.com",
      "bustlingbook.com",
      "butterbulb.com",
      "butterburst.com",
      "cakesdrum.com",
      "calculatingcircle.com",
      "calculatingtoothbrush.com",
      "callousbrake.com",
      "calmcactus.com",
      "calypsocapsule.com",
      "cannonchange.com",
      "capriciouscorn.com",
      "captivatingcanyon.com",
      "captivatingillusion.com",
      "captivatingpanorama.com",
      "captivatingperformance.com",
      "carefuldolls.com",
      "caringcast.com",
      "caringzinc.com",
      "carloforward.com",
      "carscannon.com",
      "cartkitten.com",
      "carvecakes.com",
      "catalogcake.com",
      "catschickens.com",
      "cattlecommittee.com",
      "causecherry.com",
      "cautiouscamera.com",
      "cautiouscherries.com",
      "cautiouscrate.com",
      "cautiouscredit.com",
      "cavecurtain.com",
      "ceciliavenus.com",
      "celestialeuphony.com",
      "celestialquasar.com",
      "celestialspectra.com",
      "chaireggnog.com",
      "chairscrack.com",
      "chairsdonkey.com",
      "chalkoil.com",
      "changeablecats.com",
      "channelcamp.com",
      "chargecracker.com",
      "charmingplate.com",
      "charscroll.com",
      "cheerycraze.com",
      "chessbranch.com",
      "chesscolor.com",
      "chesscrowd.com",
      "childlikecrowd.com",
      "childlikeexample.com",
      "childlikeform.com",
      "chilledliquid.com",
      "chingovernment.com",
      "chinsnakes.com",
      "chipperisle.com",
      "chivalrouscord.com",
      "chubbycreature.com",
      "chunkycactus.com",
      "cicdserver.com",
      "cinemabonus.com",
      "circlelevel.com",
      "clammychicken.com",
      "cleanhaircut.com",
      "cloisteredcurve.com",
      "closedcows.com",
      "closefriction.com",
      "cloudhustles.com",
      "cloudjumbo.com",
      "clumsycar.com",
      "coatfood.com",
      "cobaltoverture.com",
      "coffeesidehustle.com",
      "coldbalance.com",
      "coldcreatives.com",
      "colorfulafterthought.com",
      "colossalclouds.com",
      "colossalcoat.com",
      "colossalcry.com",
      "combativecar.com",
      "combativedetail.com",
      "combbit.com",
      "combcattle.com",
      "combcompetition.com",
      "comfortablecheese.com",
      "comparereaction.com",
      "compiledoctor.com",
      "concernedchange.com",
      "concernedchickens.com",
      "condemnedcomb.com",
      "conditionchange.com",
      "conditioncrush.com",
      "confesschairs.com",
      "configchain.com",
      "connectashelf.com",
      "consciouschairs.com",
      "consciouscheese.com",
      "consumerzero.com",
      "controlcola.com",
      "controlhall.com",
      "convertbatch.com",
      "cooingcoal.com",
      "coordinatedbedroom.com",
      "coordinatedcoat.com",
      "copycarpenter.com",
      "copyrightaccesscontrols.com",
      "coralreverie.com",
      "corgibeachday.com",
      "cosmicsculptor.com",
      "cosmosjackson.com",
      "courageousbaby.com",
      "cozydusk.com",
      "cozyhillside.com",
      "cozytryst.com",
      "crabbychin.com",
      "crackedsafe.com",
      "crafthenry.com",
      "crashchance.com",
      "cratecamera.com",
      "craterbox.com",
      "creatorcherry.com",
      "creatorpassenger.com",
      "creaturecabbage.com",
      "crimsonmeadow.com",
      "critictruck.com",
      "crookedcreature.com",
      "crowdedmass.com",
      "cryptvalue.com",
      "crystalboulevard.com",
      "cubchannel.com",
      "cubepins.com",
      "cuddlycake.com",
      "cuddlylunchroom.com",
      "culturedcamera.com",
      "culturedfeather.com",
      "cumbersomecar.com",
      "cumbersomecarpenter.com",
      "curiouschalk.com",
      "curioussuccess.com",
      "curlycannon.com",
      "currentcollar.com",
      "curtaincows.com",
      "curvedhoney.com",
      "curvycord.com",
      "curvycry.com",
      "cushiondrum.com",
      "cushionpig.com",
      "cutechin.com",
      "cyclopsdial.com",
      "dailydivision.com",
      "damagedadvice.com",
      "damageddistance.com",
      "dampdock.com",
      "dancemistake.com",
      "dandydune.com",
      "dandyglow.com",
      "dapperdiscussion.com",
      "datastoried.com",
      "daughterstone.com",
      "daymodern.com",
      "dazzlingbook.com",
      "deafeningdock.com",
      "deafeningdowntown.com",
      "debonairdust.com",
      "debonairtree.com",
      "debugentity.com",
      "decidedrum.com",
      "decisivedrawer.com",
      "decisiveducks.com",
      "decoycreation.com",
      "deerbeginner.com",
      "defeatedbadge.com",
      "defensevest.com",
      "degreechariot.com",
      "delegatediscussion.com",
      "delicatecascade.com",
      "deliciousducks.com",
      "deltafault.com",
      "deluxecrate.com",
      "dependenttrip.com",
      "desirebucket.com",
      "desiredirt.com",
      "detailedgovernment.com",
      "detectdinner.com",
      "detourgame.com",
      "deviceseal.com",
      "deviceworkshop.com",
      "devilishdinner.com",
      "dewdroplagoon.com",
      "difficultfog.com",
      "digestiondrawer.com",
      "dinnerquartz.com",
      "diplomahawaii.com",
      "direfuldesk.com",
      "disagreeabledrop.com",
      "discreetquarter.com",
      "distributionneck.com",
      "distributionpocket.com",
      "distributiontomatoes.com",
      "disturbedquiet.com",
      "divehope.com",
      "dk4ywix.com",
      "dockdigestion.com",
      "dogsonclouds.com",
      "dollardelta.com",
      "doubledefend.com",
      "doubtdrawer.com",
      "dq95d35.com",
      "drainpaste.com",
      "dramaticdirection.com",
      "driftpizza.com",
      "drollwharf.com",
      "drydrum.com",
      "dustydime.com",
      "dustyhammer.com",
      "eagereden.com",
      "eagerflame.com",
      "eagerknight.com",
      "earthyfarm.com",
      "eatablesquare.com",
      "echoinghaven.com",
      "effervescentcoral.com",
      "effervescentvista.com",
      "effulgentnook.com",
      "effulgenttempest.com",
      "ejyymghi.com",
      "elasticchange.com",
      "elderlybean.com",
      "elderlytown.com",
      "elephantqueue.com",
      "elusivebreeze.com",
      "elusivecascade.com",
      "elysiantraverse.com",
      "embellishedmeadow.com",
      "embermosaic.com",
      "emberwhisper.com",
      "eminentbubble.com",
      "eminentend.com",
      "emptyescort.com",
      "enchantedskyline.com",
      "enchantingdiscovery.com",
      "enchantingenchantment.com",
      "enchantingmystique.com",
      "enchantingtundra.com",
      "enchantingvalley.com",
      "encourageshock.com",
      "encouragingthread.com",
      "endlesstrust.com",
      "endurablebulb.com",
      "energeticexample.com",
      "energeticladybug.com",
      "engineergrape.com",
      "engineertrick.com",
      "enigmaticblossom.com",
      "enigmaticcanyon.com",
      "enigmaticvoyage.com",
      "enormousearth.com",
      "enormousfoot.com",
      "enterdrama.com",
      "entertainskin.com",
      "enthusiastictemper.com",
      "enviousshape.com",
      "enviousthread.com",
      "equablekettle.com",
      "etherealbamboo.com",
      "ethereallagoon.com",
      "etherealpinnacle.com",
      "etherealquasar.com",
      "etherealripple.com",
      "evanescentedge.com",
      "evasivejar.com",
      "eventexistence.com",
      "exampleshake.com",
      "excitingtub.com",
      "exclusivebrass.com",
      "executeknowledge.com",
      "exhibitsneeze.com",
      "expansioneggnog.com",
      "exquisiteartisanship.com",
      "extractobservation.com",
      "extralocker.com",
      "extramonies.com",
      "exuberantedge.com",
      "facilitatebreakfast.com",
      "fadechildren.com",
      "fadedsnow.com",
      "fadewaves.com",
      "fairfeeling.com",
      "fairiesbranch.com",
      "fairytaleflame.com",
      "fallaciousfifth.com",
      "falseframe.com",
      "familiarrod.com",
      "fancyactivity.com",
      "fancydune.com",
      "fancygrove.com",
      "fangfeeling.com",
      "fantastictone.com",
      "farethief.com",
      "farmergoldfish.com",
      "farshake.com",
      "farsnails.com",
      "fastenfather.com",
      "fasterfineart.com",
      "fasterjson.com",
      "fatcoil.com",
      "faucetfoot.com",
      "faultycanvas.com",
      "fearfulfish.com",
      "fearfulmint.com",
      "fearlessfaucet.com",
      "fearlesstramp.com",
      "featherstage.com",
      "feeblestamp.com",
      "feignedfaucet.com",
      "fewjuice.com",
      "fewkittens.com",
      "finalizeforce.com",
      "finestpiece.com",
      "finitecube.com",
      "firecatfilms.com",
      "fireworkcamp.com",
      "firstendpoint.com",
      "firstfrogs.com",
      "firsttexture.com",
      "fivesidedsquare.com",
      "fixedfold.com",
      "flakyfeast.com",
      "flameuncle.com",
      "flimsycircle.com",
      "flimsythought.com",
      "flippedfunnel.com",
      "floodprincipal.com",
      "flourishingcollaboration.com",
      "flourishingendeavor.com",
      "flourishinginnovation.com",
      "flourishingpartnership.com",
      "flowersornament.com",
      "flowerycreature.com",
      "floweryfact.com",
      "floweryoperation.com",
      "flutteringfireman.com",
      "foambench.com",
      "followborder.com",
      "forecasttiger.com",
      "foretellfifth.com",
      "forevergears.com",
      "forgetfulflowers.com",
      "forgetfulsnail.com",
      "fortunatemark.com",
      "fractalcoast.com",
      "framebanana.com",
      "franticroof.com",
      "frazzleart.com",
      "freezingbuilding.com",
      "frequentflesh.com",
      "friendlycrayon.com",
      "friendlyfold.com",
      "frightenedpotato.com",
      "frogtray.com",
      "fronttoad.com",
      "frugalfiestas.com",
      "fumblingform.com",
      "functionalcrown.com",
      "functionalfeather.com",
      "funoverbored.com",
      "funoverflow.com",
      "furryfork.com",
      "furryhorses.com",
      "futuristicapparatus.com",
      "futuristicfairies.com",
      "futuristicfifth.com",
      "futuristicframe.com",
      "fuzzyaudio.com",
      "fuzzybasketball.com",
      "fuzzyerror.com",
      "fvl1f.pw",
      "gardenovens.com",
      "gaudyairplane.com",
      "geekactive.com",
      "generalprose.com",
      "generateoffice.com",
      "giantsvessel.com",
      "giddycoat.com",
      "giraffepiano.com",
      "gitcrumbs.com",
      "givevacation.com",
      "gladglen.com",
      "gladysway.com",
      "glamhawk.com",
      "gleamingcow.com",
      "gleaminghaven.com",
      "glisteningguide.com",
      "glisteningsign.com",
      "glitteringbrook.com",
      "gloriousbeef.com",
      "glowingmeadow.com",
      "gluedpixel.com",
      "goldfishgrowth.com",
      "gondolagnome.com",
      "goodbark.com",
      "gracefulmilk.com",
      "grainmass.com",
      "grandfatherguitar.com",
      "gravitykick.com",
      "grayoranges.com",
      "grayreceipt.com",
      "greyinstrument.com",
      "gripcorn.com",
      "groovyornament.com",
      "grouchybrothers.com",
      "grouchypush.com",
      "grumpydime.com",
      "grumpydrawer.com",
      "guardeddirection.com",
      "guardedschool.com",
      "guessdetail.com",
      "guidecent.com",
      "guildalpha.com",
      "guiltlessbasketball.com",
      "gulliblegrip.com",
      "gustocooking.com",
      "gustygrandmother.com",
      "h78xb.pw",
      "habitualhumor.com",
      "halcyoncanyon.com",
      "halcyonsculpture.com",
      "hallowedinvention.com",
      "haltingdivision.com",
      "hammerhearing.com",
      "handleteeth.com",
      "handnorth.com",
      "handsomehose.com",
      "handsomeindustry.com",
      "handsomelyhealth.com",
      "handsomelythumb.com",
      "handsomeyam.com",
      "handyfireman.com",
      "handyincrease.com",
      "haplesshydrant.com",
      "haplessland.com",
      "happysponge.com",
      "harborcub.com",
      "harmonicbamboo.com",
      "harmonywing.com",
      "headydegree.com",
      "headyhook.com",
      "healflowers.com",
      "hearinglizards.com",
      "heartbreakingmind.com",
      "heavydetail.com",
      "heavyplayground.com",
      "helpcollar.com",
      "helpflame.com",
      "hfc195b.com",
      "highfalutinbox.com",
      "highfalutinhoney.com",
      "hilariouszinc.com",
      "historicalbeam.com",
      "hollowafterthought.com",
      "homelycrown.com",
      "honeybulb.com",
      "honeywhipped.com",
      "honorablehydrant.com",
      "hospitablehall.com",
      "hospitablehat.com",
      "howdyinbox.com",
      "humdrumhobbies.com",
      "humdrumtouch.com",
      "hurtgrape.com",
      "hypnoticwound.com",
      "hystericalcloth.com",
      "hystericalfinger.com",
      "i9w8p.pw",
      "idolscene.com",
      "idyllicjazz.com",
      "illinvention.com",
      "illustriousoatmeal.com",
      "immensehoney.com",
      "imminentshake.com",
      "importantmeat.com",
      "importedincrease.com",
      "importedinsect.com",
      "importlocate.com",
      "impossibleexpansion.com",
      "impossiblemove.com",
      "impulsejewel.com",
      "impulselumber.com",
      "incomehippo.com",
      "incompetentjoke.com",
      "inconclusiveaction.com",
      "infamousstream.com",
      "innocentlamp.com",
      "innocentwax.com",
      "inputicicle.com",
      "inquisitiveice.com",
      "inquisitiveinvention.com",
      "intelligentscissors.com",
      "interestdust.com",
      "internalcondition.com",
      "internalsink.com",
      "irritatingfog.com",
      "itemslice.com",
      "ivykiosk.com",
      "j93557g.com",
      "jadeitite.com",
      "jaderooster.com",
      "jailbulb.com",
      "joblessdrum.com",
      "jollylens.com",
      "joyfulkeen.com",
      "joyoussurprise.com",
      "jubilantaura.com",
      "jubilantcanyon.com",
      "jubilantcascade.com",
      "jubilantglimmer.com",
      "jubilanttempest.com",
      "jubilantwhisper.com",
      "justicejudo.com",
      "k54nw.pw",
      "kaputquill.com",
      "keenquill.com",
      "kindhush.com",
      "kitesquirrel.com",
      "knitstamp.com",
      "knottyswing.com",
      "laboredlocket.com",
      "lameletters.com",
      "lamplow.com",
      "largebrass.com",
      "lasttaco.com",
      "leaplunchroom.com",
      "leftliquid.com",
      "lemonpackage.com",
      "lemonsandjoy.com",
      "liftedknowledge.com",
      "lightenafterthought.com",
      "lighttalon.com",
      "livelumber.com",
      "livelylaugh.com",
      "livelyreward.com",
      "livingsleet.com",
      "lizardslaugh.com",
      "loadsurprise.com",
      "lonelyflavor.com",
      "longingtrees.com",
      "lorenzourban.com",
      "losslace.com",
      "loudlunch.com",
      "lovelydrum.com",
      "loveseashore.com",
      "lp3tdqle.com",
      "ludicrousarch.com",
      "lumberamount.com",
      "luminousboulevard.com",
      "luminouscatalyst.com",
      "luminoussculptor.com",
      "lumpygnome.com",
      "lumpylumber.com",
      "lunchroomlock.com",
      "lustroushaven.com",
      "lyricshook.com",
      "madebyintent.com",
      "magicaljoin.com",
      "magnetairport.com",
      "majesticmountainrange.com",
      "majesticwaterscape.com",
      "majesticwilderness.com",
      "maliciousmusic.com",
      "managedpush.com",
      "mantrafox.com",
      "marblediscussion.com",
      "markedmeasure.com",
      "marketspiders.com",
      "marriedmailbox.com",
      "marriedvalue.com",
      "massivemark.com",
      "materialisticmoon.com",
      "materialmilk.com",
      "materialplayground.com",
      "meadowlullaby.com",
      "measlymiddle.com",
      "meatydime.com",
      "meddleplant.com",
      "mediatescarf.com",
      "mediumshort.com",
      "mellowhush.com",
      "mellowmailbox.com",
      "melodiouschorus.com",
      "melodiouscomposition.com",
      "meltmilk.com",
      "memopilot.com",
      "memorizematch.com",
      "memorizeneck.com",
      "mentorsticks.com",
      "meremark.com",
      "merequartz.com",
      "merryopal.com",
      "merryvault.com",
      "messagenovice.com",
      "messyoranges.com",
      "mightyspiders.com",
      "mimosamajor.com",
      "mindfulgem.com",
      "minorcattle.com",
      "minusmental.com",
      "minuteburst.com",
      "miscreantmoon.com",
      "mistyhorizon.com",
      "mittencattle.com",
      "mixedreading.com",
      "modularmental.com",
      "monacobeatles.com",
      "moorshoes.com",
      "morefriendly.com",
      "motionflowers.com",
      "motionlessbelief.com",
      "motionlessmeeting.com",
      "movemeal.com",
      "muddledaftermath.com",
      "muddledmemory.com",
      "mundanenail.com",
      "mundanepollution.com",
      "mushywaste.com",
      "muteknife.com",
      "mutemailbox.com",
      "mysticalagoon.com",
      "naivestatement.com",
      "nappyattack.com",
      "nappyneck.com",
      "neatshade.com",
      "nebulacrescent.com",
      "nebulajubilee.com",
      "nebulousamusement.com",
      "nebulousgarden.com",
      "nebulousquasar.com",
      "nebulousripple.com",
      "needlessnorth.com",
      "needyneedle.com",
      "neighborlywatch.com",
      "niftygraphs.com",
      "niftyhospital.com",
      "niftyjelly.com",
      "nightwound.com",
      "nimbleplot.com",
      "nocturnalloom.com",
      "nocturnalmystique.com",
      "noiselessplough.com",
      "nonchalantnerve.com",
      "nondescriptcrowd.com",
      "nondescriptnote.com",
      "nondescriptstocking.com",
      "nostalgicknot.com",
      "nostalgicneed.com",
      "notifyglass.com",
      "nudgeduck.com",
      "nullnorth.com",
      "numerousnest.com",
      "oafishchance.com",
      "oafishobservation.com",
      "obscenesidewalk.com",
      "observantice.com",
      "oldfashionedoffer.com",
      "omgthink.com",
      "omniscientfeeling.com",
      "onlywoofs.com",
      "opalquill.com",
      "operationchicken.com",
      "operationnail.com",
      "oppositeoperation.com",
      "optimallimit.com",
      "opulentsylvan.com",
      "orientedargument.com",
      "orionember.com",
      "ourblogthing.com",
      "overkick.com",
      "overratedchalk.com",
      "owlsr.us",
      "oxygenfuse.com",
      "pailcrime.com",
      "pailpatch.com",
      "painstakingpickle.com",
      "paintpear.com",
      "paleleaf.com",
      "pamelarandom.com",
      "panoramicplane.com",
      "parallelbulb.com",
      "pardonpopular.com",
      "parentpicture.com",
      "parsimoniouspolice.com",
      "partplanes.com",
      "passivepolo.com",
      "pastoralroad.com",
      "pawsnug.com",
      "peacefullimit.com",
      "pedromister.com",
      "pedropanther.com",
      "perceivequarter.com",
      "perkyjade.com",
      "petiteumbrella.com",
      "photographpan.com",
      "piespower.com",
      "piquantgrove.com",
      "piquantmeadow.com",
      "piquantpigs.com",
      "piquantprice.com",
      "piquantvortex.com",
      "pixeledhub.com",
      "pizzasnut.com",
      "placeframe.com",
      "placidactivity.com",
      "placidperson.com",
      "planebasin.com",
      "playfulriver.com",
      "pleasantpump.com",
      "plotparent.com",
      "plotrabbit.com",
      "pluckypocket.com",
      "pluckyzone.com",
      "pocketfaucet.com",
      "poeticpackage.com",
      "pointdigestion.com",
      "pointlesshour.com",
      "pointlesspocket.com",
      "pointlessprofit.com",
      "pointlessrifle.com",
      "polarismagnet.com",
      "polishedcrescent.com",
      "polishedfolly.com",
      "politeplanes.com",
      "politicalporter.com",
      "popplantation.com",
      "possiblepencil.com",
      "potatoinvention.com",
      "powderjourney.com",
      "powerfulblends.com",
      "preciousplanes.com",
      "prefixpatriot.com",
      "prepareplanes.com",
      "presetrabbits.com",
      "previousplayground.com",
      "pricklypollution.com",
      "pristinegale.com",
      "probablepartner.com",
      "processplantation.com",
      "producepickle.com",
      "productsurfer.com",
      "profitrumour.com",
      "promiseair.com",
      "propertypotato.com",
      "protestcopy.com",
      "psychedelicarithmetic.com",
      "psychedelicchess.com",
      "publicsofa.com",
      "puffyloss.com",
      "puffypaste.com",
      "puffypull.com",
      "puffypurpose.com",
      "pulsatingmeadow.com",
      "pumpedpancake.com",
      "pumpedpurpose.com",
      "punyplant.com",
      "puppytooth.com",
      "purposepipe.com",
      "quaintcan.com",
      "quaintlake.com",
      "quantumlagoon.com",
      "quantumshine.com",
      "queenskart.com",
      "quillkick.com",
      "quirkybliss.com",
      "quirkysugar.com",
      "quixoticnebula.com",
      "rabbitbreath.com",
      "rabbitrifle.com",
      "radiantcanopy.com",
      "radiantlullaby.com",
      "radiateprose.com",
      "railwaygiraffe.com",
      "railwayreason.com",
      "raintwig.com",
      "rainyhand.com",
      "rainyrule.com",
      "rambunctiousflock.com",
      "rangecake.com",
      "rangeplayground.com",
      "raresummer.com",
      "reactjspdf.com",
      "readingguilt.com",
      "readymoon.com",
      "readysnails.com",
      "realizedoor.com",
      "realizerecess.com",
      "rebelclover.com",
      "rebelhen.com",
      "rebelswing.com",
      "receiptcent.com",
      "receptiveink.com",
      "receptivereaction.com",
      "recessrain.com",
      "reconditeprison.com",
      "reconditerake.com",
      "reconditerespect.com",
      "refundradar.com",
      "regularplants.com",
      "regulatesleet.com",
      "rehabilitatereason.com",
      "relationrest.com",
      "releasepath.com",
      "reloadphoto.com",
      "rememberdiscussion.com",
      "rentinfinity.com",
      "replaceroute.com",
      "resonantbrush.com",
      "resonantrock.com",
      "respectrain.com",
      "resplendentecho.com",
      "restrainstorm.com",
      "restructureinvention.com",
      "retrievemint.com",
      "rhetoricalactivity.com",
      "rhetoricalloss.com",
      "rhetoricalveil.com",
      "rhymezebra.com",
      "rhythmrule.com",
      "richstring.com",
      "righteouscrayon.com",
      "rigidrobin.com",
      "rigidveil.com",
      "rigorlab.com",
      "ringplant.com",
      "ringsrecord.com",
      "ritzykey.com",
      "ritzyrepresentative.com",
      "ritzyveil.com",
      "rockpebbles.com",
      "rollconnection.com",
      "roseincome.com",
      "rottenray.com",
      "ruralrobin.com",
      "rusticprice.com",
      "ruthlessdegree.com",
      "ruthlessmilk.com",
      "sableloss.com",
      "sablesmile.com",
      "sadloaf.com",
      "saffronrefuge.com",
      "sagargift.com",
      "saltsacademy.com",
      "samesticks.com",
      "samplesamba.com",
      "sandstrophies.com",
      "satisfycork.com",
      "scarcecard.com",
      "scarceshock.com",
      "scarcesign.com",
      "scarcestructure.com",
      "scarcesurprise.com",
      "scaredcomfort.com",
      "scaredsidewalk.com",
      "scaredslip.com",
      "scaredsnake.com",
      "scaredsnakes.com",
      "scaredsong.com",
      "scaredstomach.com",
      "scaredswing.com",
      "scarefowl.com",
      "scarfsmash.com",
      "scatteredheat.com",
      "scatteredstream.com",
      "scenicapparel.com",
      "scientificshirt.com",
      "scintillatingscissors.com",
      "scintillatingsilver.com",
      "scissorsstatement.com",
      "scrapesleep.com",
      "scratchsofa.com",
      "screechingfurniture.com",
      "screechingstocking.com",
      "screechingstove.com",
      "scribblestring.com",
      "scrollservice.com",
      "scrubswim.com",
      "secondhandfall.com",
      "secretivesheep.com",
      "secretspiders.com",
      "secretturtle.com",
      "seedscissors.com",
      "selfishsea.com",
      "sendingspire.com",
      "sensorsmile.com",
      "separatesort.com",
      "seraphichorizon.com",
      "seraphicjubilee.com",
      "serendipityecho.com",
      "serenecascade.com",
      "serenepebble.com",
      "serenesurf.com",
      "serioussuit.com",
      "serpentshampoo.com",
      "settleshoes.com",
      "shadeship.com",
      "shaggytank.com",
      "shakyseat.com",
      "shakysurprise.com",
      "shakytaste.com",
      "shallowblade.com",
      "shamerain.com",
      "shapecomb.com",
      "sheargovernor.com",
      "shesubscriptions.com",
      "shinypond.com",
      "shirtsidewalk.com",
      "shiveringspot.com",
      "shiverscissors.com",
      "shockinggrass.com",
      "shockingship.com",
      "shredquiz.com",
      "shrillspoon.com",
      "shydinosaurs.com",
      "sierrakermit.com",
      "signaturepod.com",
      "siliconslow.com",
      "sillyscrew.com",
      "simplesidewalk.com",
      "simulateswing.com",
      "sincerebuffalo.com",
      "sincerepelican.com",
      "sinceresubstance.com",
      "singroot.com",
      "sixscissors.com",
      "sizzlingsmoke.com",
      "skillfuldrop.com",
      "skisofa.com",
      "slaysweater.com",
      "slimyscarf.com",
      "slinksuggestion.com",
      "slopesoap.com",
      "smallershops.com",
      "smashshoe.com",
      "smashsurprise.com",
      "smoggysnakes.com",
      "smoggysongs.com",
      "smoggystation.com",
      "snacktoken.com",
      "snakemineral.com",
      "snakeslang.com",
      "snoresmile.com",
      "snowmentor.com",
      "soggysponge.com",
      "solarislabyrinth.com",
      "somberscarecrow.com",
      "sombersticks.com",
      "songsterritory.com",
      "soothingglade.com",
      "soresidewalk.com",
      "soresneeze.com",
      "sorethunder.com",
      "soretrain.com",
      "sortsummer.com",
      "sparklingshelf.com",
      "specialscissors.com",
      "spellmist.com",
      "spellsalsa.com",
      "spiffymachine.com",
      "spirebaboon.com",
      "spookyexchange.com",
      "spookyskate.com",
      "spoonsilk.com",
      "spotlessstamp.com",
      "spottednoise.com",
      "springolive.com",
      "springsister.com",
      "springsnails.com",
      "sproutingbag.com",
      "sprydelta.com",
      "spuriousair.com",
      "spuriousbase.com",
      "spuriousstranger.com",
      "squalidscrew.com",
      "stakingbasket.com",
      "stakingshock.com",
      "stakingsmile.com",
      "staleshow.com",
      "stalesummer.com",
      "starkscale.com",
      "startingcars.com",
      "stayaction.com",
      "steadfastseat.com",
      "steadfastsystem.com",
      "stealsteel.com",
      "stepcattle.com",
      "stepwisevideo.com",
      "stereoproxy.com",
      "stereotypedsugar.com",
      "stiffgame.com",
      "stiffstem.com",
      "stimulatingsneeze.com",
      "stingsquirrel.com",
      "stingycrush.com",
      "stingyshoe.com",
      "stingyspoon.com",
      "stockingsleet.com",
      "stockingsneeze.com",
      "stomachscience.com",
      "stonechin.com",
      "stopstomach.com",
      "stormyachiever.com",
      "straightnest.com",
      "strangeclocks.com",
      "strangersponge.com",
      "strangesink.com",
      "streetsort.com",
      "stretchsister.com",
      "stretchsneeze.com",
      "stretchsquirrel.com",
      "strivesidewalk.com",
      "strokesystem.com",
      "stupendoussnow.com",
      "stupidscene.com",
      "sublimequartz.com",
      "subsequentswim.com",
      "substantialcarpenter.com",
      "substantialgrade.com",
      "successfulscent.com",
      "suggestionbridge.com",
      "sulkycook.com",
      "summerobject.com",
      "superchichair.com",
      "superficialspring.com",
      "superficialsquare.com",
      "swankysquare.com",
      "swellstocking.com",
      "swingslip.com",
      "swordgoose.com",
      "synonymoussticks.com",
      "tangibleteam.com",
      "tangycover.com",
      "tastelesstrees.com",
      "tastelesstrucks.com",
      "tastesnake.com",
      "tearfulglass.com",
      "tediousbear.com",
      "tediousticket.com",
      "teenytinycellar.com",
      "teenytinyshirt.com",
      "tempertrick.com",
      "tempttalk.com",
      "terrifictooth.com",
      "testadmiral.com",
      "texturetrick.com",
      "thicktrucks.com",
      "thingsafterthought.com",
      "thinkitwice.com",
      "thirdrespect.com",
      "thoughtlessknot.com",
      "threetruck.com",
      "ticketaunt.com",
      "ticklesign.com",
      "tiredthroat.com",
      "tiresomethunder.com",
      "toolcapital.com",
      "tradetooth.com",
      "tranquilcan.com",
      "tranquilcanyon.com",
      "tranquilside.com",
      "tranquilveil.com",
      "treadbun.com",
      "tritebadge.com",
      "tritetongue.com",
      "troubleshade.com",
      "truckstomatoes.com",
      "truculentrate.com",
      "tumbleicicle.com",
      "twistloss.com",
      "twistsweater.com",
      "ubiquitoussea.com",
      "ubiquitousyard.com",
      "unablehope.com",
      "unaccountablecreator.com",
      "unaccountablepie.com",
      "unarmedindustry.com",
      "unbecominghall.com",
      "unbecominglamp.com",
      "uncoveredexpert.com",
      "understoodocean.com",
      "unequalbrake.com",
      "unequaltrail.com",
      "unknowncontrol.com",
      "unknowncrate.com",
      "unknowntray.com",
      "untidyquestion.com",
      "untidyrice.com",
      "unusualtitle.com",
      "unwieldyhealth.com",
      "unwieldyimpulse.com",
      "unwieldyplastic.com",
      "uselesslumber.com",
      "validmemo.com",
      "vanfireworks.com",
      "velvetnova.com",
      "velvetquasar.com",
      "vengefulgrass.com",
      "venomousvessel.com",
      "venusgloria.com",
      "verdantanswer.com",
      "verdantloom.com",
      "verseballs.com",
      "vibrantcelebration.com",
      "vibrantpact.com",
      "vibranttalisman.com",
      "vibrantvale.com",
      "virtualvincent.com",
      "vividfrost.com",
      "volatileprofit.com",
      "volatilevessel.com",
      "voraciousgrip.com",
      "wantingwindow.com",
      "warmafterthought.com",
      "warmquiver.com",
      "wateryvan.com",
      "websitesdude.com",
      "wellgroomedapparel.com",
      "wellgroomedhydrant.com",
      "whimsicalcanyon.com",
      "whimsicalgrove.com",
      "whirlwealth.com",
      "whisperingcascade.com",
      "whisperingquasar.com",
      "whisperingsummit.com",
      "wildcommittee.com",
      "wirecomic.com",
      "wiredforcoffee.com",
      "wittypopcorn.com",
      "worriednumber.com",
      "wretchedfloor.com",
      "wtaccesscontrol.com",
      "xovq5nemr.com",
      "yieldingwoman.com",
      "zephyrlabyrinth.com",
      "zestycrime.com",
      "zlp6s.pw",
      "admixer.co.kr",
      "admixer.net",
      "admobile.com",
      "admotion.com",
      "adnami.io",
      "adnet.de",
      "adnetwork.net",
      "adnium.com",
      "adnologies.com",
      "heias.com",
      "2o7.net",
      "adsymptotic.com",
      "auditude.com",
      "bizible.com",
      "bizibly.com",
      "demdex.com",
      "demdex.net",
      "dmtracker.com",
      "efrontier.com",
      "everestads.net",
      "everestjs.net",
      "everesttech.net",
      "fyre.co",
      "hitbox.com",
      "marketo.com",
      "marketo.net",
      "omniture.com",
      "omtrdc.net",
      "tmogul.com",
      "touchclarity.com",
      "tubemogul.com",
      "adomik.com",
      "adonion.com",
      "adop.cc",
      "clickotmedia.com",
      "adotmob.com",
      "adpepper.com",
      "adpepper.us",
      "adperfect.com",
      "adperium.com",
      "adpersia.com",
      "adprs.net",
      "aprecision.net",
      "adpredictive.com",
      "adreactor.com",
      "adrevolution.com",
      "adriver.ru",
      "adscale.de",
      "adscience.nl",
      "adserverpub.com",
      "adshuffle.com",
      "adside.com",
      "adskeeper.co.uk",
      "adskeeper.com",
      "adsfac.eu",
      "adsfac.net",
      "adsfac.us",
      "facilitatedigital.com",
      "adspeed.com",
      "adspeed.net",
      "adsperity.com",
      "adbroker.de",
      "adspirit.com",
      "adspirit.de",
      "adspirit.net",
      "adsquare.com",
      "adsrevenue.net",
      "adstanding.com",
      "ad-stir.com",
      "adstours.com",
      "clickintext.net",
      "4dsply.com",
      "adsupply.com",
      "adtegrity.com",
      "adtegrity.net",
      "adtelligence.de",
      "adtelligent.com",
      "vertamedia.com",
      "adentifi.com",
      "adtiger.de",
      "zadn.vn",
      "adtruth.com",
      "adultadworld.com",
      "adultmoda.com",
      "ad4m.at",
      "ad4mat.de",
      "advangelists.com",
      "adventive.com",
      "adventori.com",
      "adnext.fr",
      "adv-adserver.com",
      "adversal.com",
      "adsmart.com",
      "adverticum.com",
      "adverticum.net",
      "advertise.com",
      "advertisespace.com",
      "advertstream.com",
      "advisormedia.cz",
      "adworx.at",
      "adworx.be",
      "adworx.nl",
      "adx1.com",
      "adxbid.info",
      "adxpansion.com",
      "00px.net",
      "adxvalue.com",
      "adxvalue.de",
      "adyard.de",
      "adyield.com",
      "adyoulike.com",
      "omnitagjs.com",
      "adzcentral.com",
      "adzly.com",
      "aerifymedia.com",
      "anonymous-media.com",
      "affilimate.io",
      "affine.tv",
      "affinesystems.com",
      "affinity.com",
      "atomex.net",
      "afdads.com",
      "afterdownload.com",
      "afterpay.com",
      "adgibbon.com",
      "365media.com",
      "aidata.io",
      "aidemsrv.com",
      "aim4media.com",
      "airpush.com",
      "albacross.com",
      "allstarmediagroup.com",
      "aloodo.com",
      "altergeo.ru",
      "travelaudience.com",
      "a2z.com",
      "amazon-adsystem.com",
      "amazon.ca",
      "amazon.co.jp",
      "amazon.co.uk",
      "amazon.de",
      "amazon.dev",
      "amazon.es",
      "amazon.fr",
      "amazon.it",
      "assoc-amazon.com",
      "imdb.com",
      "serving-sys.com",
      "adnetwork.vn",
      "ambientdigital.com.vn",
      "clearsearchmedia.com",
      "andbeyond.media",
      "aniview.com",
      "admission.net",
      "ansira.com",
      "ynxs.io",
      "dsply.com",
      "pixel.anyclip.com",
      "appcast.io",
      "appenda.com",
      "appflood.com",
      "appier.com",
      "appier.net",
      "applifier.com",
      "applovin.com",
      "mopub.com",
      "appsflyer.com",
      "appssavvy.com",
      "adfusion.com",
      "acint.net",
      "adxcel-ec2.com",
      "thesearchagency.com",
      "thesearchagency.net",
      "rapleaf.com",
      "atedra.com",
      "affiliatetracking.com",
      "atrinsic.com",
      "attn.tv",
      "attentivemobile.com",
      "audience2media.com",
      "audienceadnetwork.com",
      "userreport.com",
      "audrte.com",
      "audiencescience.com",
      "revsci.net",
      "targetingmarketplace.com",
      "wunderloop.net",
      "ad.gt",
      "hadronid.net",
      "cpmadvisors.com",
      "cpmatic.com",
      "optim.al",
      "orbengine.com",
      "xa.net",
      "jpush.cn",
      "am.ua",
      "autocentre.ua",
      "automattic.com",
      "pubmine.com",
      "avantlink.com",
      "avmws.com",
      "adspdbl.com",
      "avidglobalmedia.com",
      "avidglobalmedia.eu",
      "metadsp.co.uk",
      "avct.cloud",
      "avocet.io",
      "avsads.com",
      "aweber.com",
      "affili.net",
      "affilinet-inside.de",
      "banner-rotation.com",
      "buy.at",
      "digitalwindow.com",
      "dwin1.com",
      "perfiliate.com",
      "successfultogether.co.uk",
      "zanox-affiliate.de",
      "zanox.com",
      "awio.com",
      "w3counter.com",
      "ayads.co",
      "de17a.com",
      "hi-media.com",
      "inskinmedia.com",
      "madvertise.com",
      "quantum-advertising.com",
      "sublime.xyz",
      "widespace.com",
      "rsz.sk",
      "b2.ai",
      "backbeatmedia.com",
      "mediago.io",
      "popin.cc",
      "insightexpress.com",
      "insightexpressai.com",
      "bannerconnect.net",
      "barilliance.com",
      "baronsoffers.com",
      "adbrite.com",
      "basis.net",
      "sitescout.com",
      "batanga.com",
      "batanganetwork.com",
      "sumo.com",
      "sumome.com",
      "beachfront.com",
      "bfmio.com",
      "beanstockmedia.com",
      "begun.ru",
      "adbutler.de",
      "belboon.com",
      "7a75ebcbd7.com",
      "beop.io",
      "betgenius.com",
      "connextra.com",
      "betweendigital.com",
      "aivalabs.com",
      "bidfluence.com",
      "fksnk.com",
      "bidtellect.com",
      "bttrack.com",
      "bidtheatre.com",
      "bidvertiser.com",
      "bigcrunch.com",
      "bigmir.net",
      "vendemore.com",
      "bitcoinplus.com",
      "bitmedia.io",
      "blacklabelads.com",
      "blismedia.com",
      "btloader.com",
      "pagefair.com",
      "pagefair.net",
      "videoplayerhub.com",
      "blogcatalog.com",
      "theblogfrog.com",
      "blogherads.com",
      "p.brsrvr.com",
      "bluecore.com",
      "blutrumpet.com",
      "boldapps.net",
      "searchserverapi.com",
      "adlightning.com",
      "boo-box.com",
      "bookmsg.com",
      "boostbox.com.br",
      "medianet.com",
      "dep-x.com",
      "app.link",
      "branch.io",
      "brandaffinity.net",
      "brandmetrics.com",
      "rtbidder.net",
      "appboycdn.com",
      "braze.com",
      "breaktime.com.tw",
      "scupio.com",
      "adserverplus.com",
      "oridian.com",
      "ybrantdigital.com",
      "b0e8.com",
      "bc0a.com",
      "brightedge.com",
      "browsiprod.com",
      "bucksense.com",
      "burstly.com",
      "burstdirectads.com",
      "burstmedia.com",
      "burstnet.com",
      "giantrealm.com",
      "businessol.com",
      "usebutton.com",
      "beaconads.com",
      "buysellads.com",
      "carbonads.com",
      "yoggrt.com",
      "buysight.com",
      "permuto.com",
      "pulsemgr.com",
      "buzzoola.com",
      "bvmedia.ca",
      "networldmedia.com",
      "networldmedia.net",
      "pangle-ads.com",
      "c1exchange.com",
      "c3metrics.com",
      "c3tag.com",
      "cabnnr.com",
      "4info.com",
      "adhaven.com",
      "brealtime.com",
      "clearstream.tv",
      "emxdgt.com",
      "emxdigital.com",
      "cadreon.com",
      "callrail.com",
      "communicatorcorp.com",
      "campaigngrid.com",
      "capitalaudience.com",
      "capitaldata.fr",
      "cpx.to",
      "carambo.la",
      "caraytech.com.ar",
      "e-planning.net",
      "cardlytics.com",
      "zucks.net",
      "cart.ro",
      "statistics.ro",
      "carts.guru",
      "cbproads.com",
      "ccgateway.net",
      "adpdealerservices.com",
      "cobalt.com",
      "cedato.com",
      "channeladvisor.com",
      "searchmarketing.com",
      "chartboost.com",
      "chaturbate.com",
      "checkm8.com",
      "cheqzone.com",
      "clickcease.com",
      "defybrick.com",
      "ensighten.com",
      "chitika.com",
      "chitika.net",
      "choicestream.com",
      "choozle.com",
      "vpdcp.com",
      "cintnetworks.com",
      "samplicio.us",
      "cityspark.com",
      "trkn.us",
      "clearlink.com",
      "1dmp.io",
      "clickyab.com",
      "clickcertain.com",
      "clickdimensions.com",
      "bashirian.biz",
      "buckridge.link",
      "clickfrog.ru",
      "franecki.net",
      "quitzon.net",
      "reichelcormier.bid",
      "wisokykulas.bid",
      "conversiondashboard.com",
      "clickguard.com",
      "clickinc.com",
      "clicksor.com",
      "clicksor.net",
      "clicktripz.com",
      "clickwinks.com",
      "clicmanager.fr",
      "clixtell.com",
      "cloud-media.fr",
      "clovenetwork.com",
      "cognitivlabs.com",
      "cmmeglobal.com",
      "collective-media.net",
      "tumri.com",
      "tumri.net",
      "colossusssp.com",
      "bidr.io",
      "freewheel.tv",
      "fwmrm.net",
      "stickyadstv.com",
      "commander1.com",
      "compasslabs.com",
      "complex.com",
      "complexmedianetwork.com",
      "adxpose.com",
      "proximic.com",
      "clarium.io",
      "confiant-integrations.net",
      "connatix.com",
      "connectad.io",
      "ctctcdn.com",
      "consumable.com",
      "serverbid.com",
      "adrolays.com",
      "df-srv.de",
      "contaxe.com",
      "contentabc.com",
      "contentexchange.me",
      "contentful.com",
      "admailtiser.com",
      "contextin.com",
      "agencytradingdesk.net",
      "contextuads.com",
      "convergedirect.com",
      "convergetrack.com",
      "conversionruler.com",
      "conversive.nl",
      "convertkit.com",
      "cootlogix.com",
      "coremotives.com",
      "analytics-au.cloud.coveo.com",
      "analytics-ca.cloud.coveo.com",
      "analytics-eu.cloud.coveo.com",
      "analytics.cloud.coveo.com",
      "analytics.org.coveo.com",
      "analytics.orgdev.coveo.com",
      "analytics.orghipaa.coveo.com",
      "analytics.orgstg.coveo.com",
      "analyticsdev-eu.cloud.coveo.com",
      "analyticsdev.cloud.coveo.com",
      "analyticshipaa.cloud.coveo.com",
      "analyticsstg.cloud.coveo.com",
      "usageanalytics.coveo.com",
      "usageanalyticsdev.coveo.com",
      "usageanalyticshipaa.coveo.com",
      "adify.com",
      "afy11.net",
      "coxdigitalsolutions.com",
      "esm1.net",
      "thinkrealtime.com",
      "cpmstar.com",
      "crimtan.com",
      "ctnsnet.com",
      "crispmedia.com",
      "bidswitch.net",
      "criteo.com",
      "criteo.net",
      "fg8dgt.com",
      "hlserve.com",
      "hooklogic.com",
      "iponweb.com",
      "iponweb.net",
      "storetail.io",
      "crosspixel.net",
      "crosspixelmedia.com",
      "crsspxl.com",
      "kitewheel.com",
      "adtdp.com",
      "dada.pro",
      "dmxleo.com",
      "infogroup.com",
      "datamind.ru",
      "aroa.io",
      "datonics.com",
      "pro-market.net",
      "dc-storm.com",
      "stormiq.com",
      "lockerdome.com",
      "dedicatedmedia.com",
      "dedicatednetworks.com",
      "deepintent.com",
      "deliverimp.com",
      "delivr.com",
      "percentmobile.com",
      "domdex.com",
      "magnetic.com",
      "orangesoda.com",
      "otracking.com",
      "demandmedia.com",
      "indieclick.com",
      "accordantmedia.com",
      "bluestreak.com",
      "dp-dhl.com",
      "developermedia.com",
      "lqcdn.com",
      "dgit.com",
      "eyeblaster.com",
      "mdadx.com",
      "unicast.com",
      "dianomi.com",
      "did-it.com",
      "didit.com",
      "privacy-center.org",
      "wtp101.com",
      "digiseg.net",
      "impact-ad.jp",
      "digitaleast.mobi",
      "adready.com",
      "adreadypixels.com",
      "adreadytractions.com",
      "cpxadroit.com",
      "cpxinteractive.com",
      "digitalriver.com",
      "keywordmax.com",
      "netflame.cc",
      "digitaltarget.ru",
      "adcolony.com",
      "admarvel.com",
      "fyber.com",
      "heyzap.com",
      "inner-active.com",
      "tpbid.com",
      "digitize.ie",
      "directadvert.ru",
      "directresponsegroup.com",
      "ppctracking.net",
      "directtrack.com",
      "disqus.com",
      "disqusads.com",
      "disquscdn.com",
      "jsrdn.com",
      "dmpxs.com",
      "adhese.com",
      "selectablemedia.com",
      "dotmetrics.net",
      "doublepimp.com",
      "doublepositive.com",
      "lookery.com",
      "driveniq.com",
      "dstillery.com",
      "m6d.com",
      "media6degrees.com",
      "dtscout.com",
      "dtssrv.com",
      "durationmedia.net",
      "dxkulture.com",
      "dynadmic.com",
      "dyntrk.com",
      "exitjunction.com",
      "dynata.com",
      "earnify.com",
      "ebay.com",
      "ebayadservices.com",
      "ebayimg.com",
      "ebaystatic.com",
      "uplynk.com",
      "effectivemeasure.com",
      "effectivemeasure.net",
      "e-kolay.net",
      "ekolay.net",
      "eleavers.com",
      "getelevar.com",
      "usemax.de",
      "emerse.com",
      "xplosion.de",
      "enecto.com",
      "bnmla.com",
      "engagebdr.com",
      "appmetrx.com",
      "engago.com",
      "entireweb.com",
      "smadex.com",
      "epicadvertising.com",
      "theepicmediagroup.com",
      "trafficmp.com",
      "epsilon.com",
      "eqads.com",
      "sascdn.com",
      "smartadserver.com",
      "leadplace.fr",
      "ero-advertising.com",
      "eskimi.com",
      "etarget.eu",
      "etargetnet.com",
      "adtng.com",
      "trafficjunky.com",
      "trafficjunky.net",
      "adwitserver.com",
      "etineria.com",
      "etrigue.com",
      "euid.eu",
      "engineseeker.com",
      "e-volution.ai",
      "evolvemediametrics.com",
      "gorillanation.com",
      "ewaydirect.com",
      "ixs1.net",
      "777seo.com",
      "ewebse.com",
      "exdynsrv.com",
      "exoclick.com",
      "exosrv.com",
      "realsrv.com",
      "wpncdn.com",
      "audienceiq.com",
      "expo-max.com",
      "extensionfactory.com",
      "extole.io",
      "eyenewton.ru",
      "eyeviewdigital.com",
      "ezodn.com",
      "ezoic.com",
      "ezoic.net",
      "fanplayr.com",
      "fathomdelivers.com",
      "fathomseo.com",
      "fetchback.com",
      "fiftyt.com",
      "fiksu.com",
      "financialcontent.com",
      "first-id.fr",
      "adcell.de",
      "flashtalking.com",
      "spongecell.com",
      "pontiflex.com",
      "adiquity.com",
      "flite.com",
      "widgetserver.com",
      "adingo.jp",
      "fluct.jp",
      "goldspotmedia.com",
      "adparlor.com",
      "flux-cdn.com",
      "flux.jp",
      "flytxt.com",
      "fimserve.com",
      "foxonestop.com",
      "mobsmith.com",
      "myads.com",
      "glotgrx.com",
      "fout.jp",
      "deployads.com",
      "floors.dev",
      "pub.network",
      "adultfriendfinder.com",
      "ffn.com",
      "pop6.com",
      "tracking.friends2follow.com",
      "double-check.com",
      "frogsex.com",
      "fuseplatform.net",
      "futureads.com",
      "resultlinks.com",
      "servebom.com",
      "semasio.com",
      "semasio.net",
      "game-advertising-online.com",
      "games2win.com",
      "inviziads.com",
      "gammaplatform.com",
      "gamned.com",
      "pointroll.com",
      "reachlocal.com",
      "rlcdn.net",
      "rlets.com",
      "gb-world.net",
      "geistm.com",
      "adocean-global.com",
      "adocean.pl",
      "gemius.com",
      "gemius.pl",
      "genesismedia.com",
      "genesismediaus.com",
      "geniee.co.jp",
      "gssprt.jp",
      "geniusmonkey.com",
      "geoads.com",
      "geoedge.be",
      "getglue.com",
      "smrtlnks.com",
      "adhigh.net",
      "getintent.com",
      "glam.com",
      "glammedia.com",
      "fraudjs.io",
      "gleam.io",
      "glomex.com",
      "gmossp-sp.jp",
      "reemo-ad.jp",
      "gnezdo.ru",
      "godaddy.com",
      "godatafeed.com",
      "2mdn.net",
      "admeld.com",
      "admob.com",
      "adometry.com",
      "adservice.google.com",
      "adwords.google.com",
      "channelintelligence.com",
      "dartsearch.net",
      "destinationurl.com",
      "doubleclick.net",
      "fcmatch.google.com",
      "fcmatch.youtube.com",
      "google.com/pagead/1p-user-list",
      "googleadservices.com",
      "googlesyndication.com",
      "googletagservices.com",
      "invitemedia.com",
      "teracent.com",
      "teracent.net",
      "ytsa.net",
      "crm4d.com",
      "groceryshopping.net",
      "groovinads.com",
      "xad.com",
      "guj.de",
      "globo.com",
      "gumgum.com",
      "justpremium.com",
      "hands.com.br",
      "harrenmedia.com",
      "harrenmedianetwork.com",
      "htlbid.com",
      "ic-live.com",
      "icrossing.com",
      "sptag3.com",
      "hellobar.com",
      "hextom.com",
      "hilltopads.com",
      "hilltopads.net",
      "horyzon-media.com",
      "hotmart.com",
      "hotwords.com",
      "hotwords.es",
      "htplayground.com",
      "httpool.com",
      "clearbit.com",
      "clearbitjs.com",
      "hs-banner.com",
      "hs-scripts.com",
      "hsadspixel.net",
      "hsappstatic.net",
      "hscollectedforms.net",
      "hsforms.com",
      "hsleadflows.net",
      "hubapi.com",
      "huntmads.com",
      "hurra.com",
      "hybrid.ai",
      "affec.tv",
      "digitru.st",
      "iac.com",
      "i-behavior.com",
      "ib-ibi.com",
      "unica.com",
      "eu-1-id5-sync.com",
      "id5-sync.com",
      "idg.com",
      "idgtechnetwork.com",
      "kickfire.com",
      "sa-as.com",
      "visistat.com",
      "600z.com",
      "ientry.com",
      "ignitad.com",
      "illumin.com",
      "viewablemedia.net",
      "visiblemeasures.com",
      "i-mobile.co.jp",
      "clearsaleing.com",
      "csdata1.com",
      "csdata2.com",
      "csdata3.com",
      "fqtag.com",
      "impactcdn.com",
      "impactradius-event.com",
      "impactradius-go.com",
      "ojrq.net",
      "pxf.io",
      "trackonomics.net",
      "360yield.com",
      "improvedigital.com",
      "adotsolution.com",
      "casalemedia.com",
      "indexexchange.com",
      "indexww.com",
      "impressiondesk.com",
      "infectiousmedia.com",
      "gimbal.com",
      "adinsight.com",
      "adinsight.eu",
      "infolinks.com",
      "owneriq.com",
      "owneriq.net",
      "ninthdecimal.com",
      "aerserv.com",
      "inmobi.com",
      "sproutinc.com",
      "revjet.com",
      "innity.com",
      "innovid.com",
      "tvsquared.com",
      "instinctive.io",
      "instinctiveads.com",
      "insurads.com",
      "adsafemedia.com",
      "adsafeprotected.com",
      "iasds01.com",
      "integralads.com",
      "intentiq.com",
      "smct.io",
      "intentmedia.com",
      "intentmedia.net",
      "vlitag.com",
      "intergi.com",
      "intermarkets.net",
      "intermundomedia.com",
      "ibpxl.com",
      "internetbrands.com",
      "interpolls.com",
      "im-apps.net",
      "inuvo.com",
      "netseer.com",
      "investingchannel.com",
      "videostep.com",
      "videoplaza.tv",
      "invocacdn.com",
      "iperceptions.com",
      "centraliprom.com",
      "iprom.net",
      "iprom.si",
      "mediaiprom.com",
      "ipromote.com",
      "precisionclick.com",
      "iprospect.com",
      "iqm.com",
      "iqzone.com",
      "fengkongcloud.com",
      "adversalservers.com",
      "digbro.com",
      "ismatlab.com",
      "ispot.tv",
      "i.ua",
      "ivitrack.com",
      "oberon-media.com",
      "jaroop.com",
      "jasperlabs.com",
      "tradelab.fr",
      "jink.de",
      "jinkads.com",
      "jivox.com",
      "jobthread.com",
      "jads.co",
      "juicyads.com",
      "justuno.com",
      "uuidksinc.net",
      "kameleoon.com",
      "kameleoon.eu",
      "kameleoon.io",
      "kargo.com",
      "revoffers.com",
      "adzerk.com",
      "adzerk.net",
      "keyade.com",
      "keywee.co",
      "kissmyads.com",
      "103092804.com",
      "kitd.com",
      "peerset.com",
      "durchsichtig.xyz",
      "klarnaservices.com",
      "klaviyo.com",
      "config-cdn.ksearchnet.com",
      "ipv4check.ksearchnet.com",
      "ipv6check.ksearchnet.com",
      "klavyio.klevu.com",
      "moi-ai.ksearchnet.com",
      "moicustomizations.klevu.com",
      "pqa-analytics.service.ksearchnet.com",
      "stats.klevu.com",
      "stats.ksearchnet.com",
      "statsjs.klevu.com",
      "visitor.service.ksearchnet.com",
      "zjs.klevu.com",
      "zstats.klevu.com",
      "brand-display.com",
      "admost.com",
      "kokteyl.com",
      "komli.com",
      "kontera.com",
      "adsummos.net",
      "krushmedia.com",
      "ktkjmp.com",
      "kueezrtb.com",
      "getlasso.co",
      "lasso.link",
      "layer-ads.net",
      "leadbolt.com",
      "leadforensics.com",
      "leadinfo.net",
      "leanplum.com",
      "lemmatechnologies.com",
      "levexis.com",
      "lexosmedia.com",
      "lfstmedia.com",
      "lifestreetmedia.com",
      "adizio.com",
      "open-adsyield.com",
      "ortb.net",
      "linkconnector.com",
      "adf.ly",
      "listenlayer.com",
      "espssl.com",
      "listrak.com",
      "listrakbi.com",
      "livechatinc.com",
      "liadm.com",
      "liveintent.com",
      "liveinternet.ru",
      "yadro.ru",
      "liveramp.com",
      "pippio.com",
      "privacymanager.io",
      "rlcdn.com",
      "tvpixel.com",
      "lytiks.com",
      "localyokelmedia.com",
      "loomia.com",
      "loopfuse.net",
      "loopme.com",
      "loopme.me",
      "tk0x1.com",
      "crwdcntrl.net",
      "lotame.com",
      "lotlinx.com",
      "lowermybills.com",
      "lptracker.io",
      "lunamedia.live",
      "ppcprotect.com",
      "line-scdn.net",
      "line.me",
      "yahoo.co.jp",
      "madhouse.cn",
      "dinclinx.com",
      "madisonlogic.com",
      "magnify360.com",
      "adsbyisocket.com",
      "freeskreen.com",
      "isocket.com",
      "rubiconproject.com",
      "spotx.tv",
      "spotxchange.com",
      "campaign-archive1.com",
      "chimpstatic.com",
      "list-manage.com",
      "mailchimp.com",
      "bannerbank.ru",
      "manifest.ru",
      "industrybrains.com",
      "marchex.com",
      "mrf.io",
      "newsroom.bi",
      "marinsm.com",
      "mapixl.com",
      "marketiq.com",
      "martiniadnetwork.com",
      "mashero.com",
      "dynamicyield.com",
      "chemistry.com",
      "match.com",
      "meetic-partners.com",
      "matheranalytics.com",
      "matomy.com",
      "matomymarket.com",
      "optimatic.com",
      "xtendmedia.com",
      "maxbounty.com",
      "mb01.com",
      "mdhv.io",
      "mdotm.com",
      "mediabrix.com",
      "mfadsrvr.com",
      "medialets.com",
      "alocdn.com",
      "designbloxlive.com",
      "mathtag.com",
      "mediamath.com",
      "media.net",
      "adbuyer.com",
      "mediaocean.com",
      "media-servers.net",
      "mediashakers.com",
      "mediatrust.com",
      "grow.me",
      "mediavine.com",
      "mediawallah.com",
      "mediawallahscript.com",
      "origo.hu",
      "medicxmedia.com",
      "megaindex.ru",
      "merchantadvantage.com",
      "rimmkaufman.com",
      "rkdms.com",
      "securedvisit.com",
      "atlassolutions.com",
      "liverail.com",
      "roiservice.com",
      "clickdistrict.com",
      "creative-serving.com",
      "dt00.net",
      "dt07.net",
      "marketgid.com",
      "mgid.com",
      "microad.jp",
      "adbureau.net",
      "adecn.com",
      "adlantic.nl",
      "adnxs-simple.com",
      "adnxs.com",
      "adrdgt.com",
      "alenty.com",
      "appnexus.com",
      "aquantive.com",
      "bat.bing.com",
      "bizo.com",
      "bizographics.com",
      "drawbrid.ge",
      "msads.net",
      "xandr.com",
      "mmismm.com",
      "minutemedia-prebid.com",
      "sendtonews.com",
      "mirando.de",
      "mountain.com",
      "steelhouse.com",
      "steelhousemedia.com",
      "mobfox.com",
      "mobials.com",
      "mobileadtrading.com",
      "showmeinn.com",
      "mobilestorm.com",
      "adfunky.com",
      "mochila.com",
      "mojiva.com",
      "certona.com",
      "monetate.com",
      "monetate.net",
      "res-x.com",
      "cpalead.com",
      "monetizemore.com",
      "monoloop.com",
      "monster.com",
      "moolahmedia.com",
      "singlefeed.com",
      "vendio.com",
      "micpn.com",
      "affbuzzads.com",
      "mrtnsvr.com",
      "mts.ru",
      "multiplestreammktg.com",
      "mundomedia.com",
      "mypressplus.com",
      "ppjol.net",
      "mythings.com",
      "mywebgrocer.com",
      "audiencemanager.de",
      "nativeads.com",
      "ntv.io",
      "postrelease.com",
      "navdmp.com",
      "navegg.com",
      "newscgp.com",
      "ad-srv.net",
      "netaffiliation.com",
      "netbina.com",
      "adelixir.com",
      "netelixir.com",
      "netmining.com",
      "netmng.com",
      "netpub.media",
      "cdnma.com",
      "net-results.com",
      "nr7.us",
      "mixpo.com",
      "newtention.net",
      "federatedmedia.net",
      "fmpub.net",
      "ibsys.com",
      "lakana.com",
      "nextdoor.com",
      "brainlyads.com",
      "nextmillmedia.com",
      "powerad.ai",
      "adroll.com",
      "1rx.io",
      "adconion.com",
      "amgdgt.com",
      "amobee.com",
      "euroclick.com",
      "gwallet.com",
      "lucidmedia.com",
      "po.st",
      "radiumone.com",
      "rhythmnewmedia.com",
      "rhythmone.com",
      "rhythmxchange.com",
      "scanscout.com",
      "tremorhub.com",
      "tremormedia.com",
      "tremorvideo.com",
      "turn.com",
      "unrulymedia.com",
      "yume.com",
      "yumenetworks.com",
      "exelate.com",
      "exelator.com",
      "imrworldwide.com",
      "vdna-assets.com",
      "visualdna.com",
      "fairfax.com.au",
      "fxj.com.au",
      "samurai-factory.jp",
      "shinobi.jp",
      "networkedblogs.com",
      "nitropay.com",
      "noktamedya.com",
      "virgul.com",
      "nordicdataresources.net",
      "stackla.com",
      "adleadevent.com",
      "mediavoice.com",
      "polarcdn-engine.com",
      "polarcdn-pentos.com",
      "polarcdn-terrax.com",
      "polarcdn.com",
      "polarmobile.com",
      "nrelate.com",
      "nrich.ai",
      "nuffnang.com",
      "nuffnang.com.my",
      "nugg.ad",
      "nuggad.net",
      "presage.io",
      "adohana.com",
      "ohana-media.com",
      "ohanaqb.com",
      "accuenmedia.com",
      "omnicomgroup.com",
      "omnisnippet1.com",
      "onaudience.com",
      "airpr.com",
      "guoshipartners.com",
      "onevision.com.tw",
      "onef.pro",
      "itsoneiota.com",
      "oneiota.co.uk",
      "onesignal.com",
      "onetag-sys.com",
      "oneupweb.com",
      "sodoit.com",
      "online-metrix.net",
      "adsafety.net",
      "onnetwork.tv",
      "spot.im",
      "spotim.market",
      "liftdna.com",
      "openx.com",
      "openx.net",
      "openx.org",
      "openxcdn.net",
      "openxenterprise.com",
      "pubnation.com",
      "servedbyopenx.com",
      "mobiletheory.com",
      "opera.com",
      "operamediaworks.com",
      "operasoftware.com",
      "opinary.com",
      "advg.jp",
      "opt.ne.jp",
      "p-advg.com",
      "opti-digital.com",
      "optify.net",
      "episerver.net",
      "optimizely.com",
      "optimumresponse.com",
      "optmd.com",
      "bkrtx.com",
      "bluekai.com",
      "en25.com",
      "estara.com",
      "grapeshot.co.uk",
      "maxymiser.com",
      "moat.com",
      "moatads.com",
      "moatpixel.com",
      "nexac.com",
      "responsys.com",
      "orbsrv.com",
      "otm-r.com",
      "otto.de",
      "ligatus.com",
      "outbrain.com",
      "outbrainimg.com",
      "sphere.com",
      "visualrevenue.com",
      "zemanta.com",
      "out-there-media.com",
      "affiliatly.com",
      "dsnextgen.com",
      "oversee.net",
      "ownpage.fr",
      "adconnexa.com",
      "adsbwm.com",
      "the-ozone-project.com",
      "paid-to-promote.net",
      "papayads.net",
      "pepperjam.com",
      "pjatr.com",
      "pjtra.com",
      "pntra.com",
      "pntrac.com",
      "pntrs.com",
      "iivt.com",
      "lzjl.com",
      "paypopup.com",
      "pbbl.co",
      "peer39.com",
      "peer39.net",
      "peerfly.com",
      "theweathernetwork.com",
      "performancing.com",
      "adtoll.com",
      "clickbooth.com",
      "legolas-media.com",
      "permodo.com",
      "permutive.app",
      "permutive.com",
      "prmutv.co",
      "pheedo.com",
      "cxense.com",
      "emediate.biz",
      "emediate.com",
      "emediate.dk",
      "emediate.eu",
      "hit-parade.com",
      "npttech.com",
      "pinpoll.com",
      "pixel.ad",
      "pixel.sg",
      "pixfuture.com",
      "piximedia.com",
      "pixlee.com",
      "turntonetworks.com",
      "platform-one.co.jp",
      "playground.xyz",
      "intergient.com",
      "playwire.com",
      "plista.com",
      "pocketcents.com",
      "popads.net",
      "popadscdn.net",
      "popcash.net",
      "poprule.com",
      "popunder.ru",
      "powerlinks.com",
      "2trk.info",
      "predictad.com",
      "blogads.com",
      "pressflex.com",
      "primis.tech",
      "sekindo.com",
      "privy.com",
      "proclivitymedia.com",
      "proclivitysystems.com",
      "pswec.com",
      "projectwonderful.com",
      "prometheusintelligencetechnology.com",
      "propellerads.com",
      "rtmark.net",
      "ad-score.com",
      "protected.media",
      "somoaudience.com",
      "publicidees.com",
      "adserver.com",
      "apmebf.com",
      "awltovhc.com",
      "cj.com",
      "conversantmedia.com",
      "dotomi.com",
      "dtmpub.com",
      "emjcd.com",
      "fastclick.com",
      "fastclick.net",
      "ftjcfx.com",
      "greystripe.com",
      "lduhtrp.net",
      "mczbf.com",
      "mediaplex.com",
      "qksz.com",
      "qksz.net",
      "runads.com",
      "rundsp.com",
      "tqlkg.com",
      "valueclick.com",
      "valueclick.net",
      "valueclickmedia.com",
      "yceml.net",
      "pch.com",
      "pubmatic.com",
      "revinet.com",
      "pbstck.com",
      "pubwise.io",
      "kanoodle.com",
      "pulse360.com",
      "seevast.com",
      "syndigonetworks.com",
      "contextweb.com",
      "datranmedia.com",
      "displaymarketplace.com",
      "pulsepoint.com",
      "p-n.io",
      "flocktory.com",
      "binlayer.com",
      "quantcast.com",
      "quantcount.com",
      "quantserve.com",
      "struq.com",
      "quantumdex.io",
      "qnsr.com",
      "qsstats.com",
      "quinstreet.com",
      "quisma.com",
      "quismatch.com",
      "xaded.com",
      "xmladed.com",
      "quora.com",
      "qwtag.com",
      "gsicommerce.com",
      "matchbin.com",
      "radiusmarketing.com",
      "linkshare.com",
      "linksynergy.com",
      "mediaforge.com",
      "nextperformance.com",
      "nxtck.com",
      "rakuten.com",
      "rmtag.com",
      "rambler.ru",
      "adthrive.com",
      "reaktion.com",
      "rebid.co",
      "rebuyengine.com",
      "redditstatic.com",
      "reduxmedia.com",
      "refersion.com",
      "convertglobal.com",
      "rekko.com",
      "reklamport.com",
      "reklamstore.com",
      "reklamz.com",
      "relestar.com",
      "relevad.com",
      "relevant-digital.com",
      "bumlam.com",
      "advertserve.com",
      "renegadeinternet.com",
      "buzzcity.com",
      "resetdigital.co",
      "nonstoppartner.net",
      "resolutionmedia.com",
      "reson8.com",
      "resonateinsights.com",
      "retargeter.com",
      "retargetly.com",
      "retirement-living.com",
      "omappapi.com",
      "optinmonster.com",
      "optnmstr.com",
      "revcontent.com",
      "revenuemax.de",
      "revtrax.com",
      "richaudience.com",
      "richrelevance.com",
      "rightaction.com",
      "mercent.com",
      "rmbn.net",
      "rmmonline.com",
      "getrockerbox.com",
      "dataxu.com",
      "dataxu.net",
      "mexad.com",
      "w55c.net",
      "rqtrk.eu",
      "rovion.com",
      "creativecdn.com",
      "rtbhouse.com",
      "rtbsystem.com",
      "rtk.io",
      "rudderlabs.com",
      "rutarget.ru",
      "saambaa.com",
      "sabavision.com",
      "reztrack.com",
      "sabre.com",
      "sabrehospitality.com",
      "shop.pe",
      "sail-horizon.com",
      "sail-personalize.com",
      "evergage.com",
      "krux.com",
      "kruxdigital.com",
      "krxd.net",
      "leadforce1.com",
      "mybuys.com",
      "plugin.management",
      "samba.tv",
      "adgear.com",
      "adgrx.com",
      "runadtag.com",
      "emarsys.net",
      "gigcount.com",
      "leadformix.com",
      "scarabresearch.com",
      "sape.ru",
      "bridgetrack.com",
      "sapient.com",
      "aimatch.com",
      "sas.com",
      "saymedia.com",
      "scandinavianadnetworks.com",
      "reflow.tv",
      "scribol.com",
      "sda.fyi",
      "searchforce.com",
      "searchforce.net",
      "seedtag.com",
      "selectmedia.asia",
      "semantiqo.com",
      "rtactivate.com",
      "sendpulse.com",
      "seoptiks.com",
      "servenobid.com",
      "sevenads.net",
      "sexinyourcity.com",
      "libertystmedia.com",
      "shareasale.com",
      "districtm.io",
      "sharethrough.com",
      "nowspots.com",
      "xiaoyuanzhao.com",
      "getshogun.com",
      "shopzilla.com",
      "shorte.st",
      "viralize.tv",
      "chocolateplatform.com",
      "ivdopia.com",
      "vdopia.com",
      "simpli.fi",
      "extend.tv",
      "zypmedia.com",
      "singular.net",
      "sng.link",
      "cookieless-data.com",
      "sddan.com",
      "adswizz.com",
      "kenshoo.com",
      "xg4ken.com",
      "skupenet.com",
      "slunecnice.cz",
      "smartclip.com",
      "smartclip.net",
      "ck-ie.com",
      "smartyads.com",
      "smi2.ru",
      "smile.io",
      "smilewanted.com",
      "smowtion.com",
      "sc-static.net",
      "snapchat.com",
      "snapengage.com",
      "cdnsnapwidget.com",
      "snapwidget.com",
      "snigelweb.com",
      "ratevoice.com",
      "photorank.me",
      "sociomantic.com",
      "socital.com",
      "sojern.com",
      "ladsp.com",
      "sonobi.com",
      "sophus3.com",
      "lijit.com",
      "prosperent.com",
      "s-onetag.com",
      "smrtb.com",
      "sovrn.com",
      "viglink.com",
      "spacechimpmedia.com",
      "adbutler.com",
      "sparklit.com",
      "sparkstudios.com",
      "spectate.com",
      "sponsorads.de",
      "sportradarserving.com",
      "wowanalytics.co.uk",
      "springserve.com",
      "nanigans.com",
      "stackadapt.com",
      "stamped.io",
      "stargamesaffiliate.com",
      "storygize.com",
      "storygize.net",
      "cams.com",
      "streamray.com",
      "strikead.com",
      "stripchat.com",
      "stripst.com",
      "strpst.com",
      "xlivrdr.com",
      "stripe.network",
      "popularmedia.com",
      "suite66.com",
      "summitmedia.co.uk",
      "adclickmedia.com",
      "sundaysky.com",
      "sunmedia.tv",
      "socdm.com",
      "survicate.com",
      "switchadhub.com",
      "switchconcepts.co.uk",
      "switchconcepts.com",
      "swoop.com",
      "cdn-sitegainer.com",
      "sitegainer.com",
      "technorati.com",
      "technoratimedia.com",
      "clickable.net",
      "syncapse.com",
      "t13.io",
      "connexity.com",
      "connexity.net",
      "perfectmarket.com",
      "pricegrabber.com",
      "skimlinks.com",
      "skimresources.com",
      "taboola.com",
      "tailsweep.com",
      "pghub.io",
      "tapad.com",
      "tapit.com",
      "tap.me",
      "tappx.com",
      "taptapnetworks.com",
      "targetix.net",
      "brid.tv",
      "tchibo.de",
      "brainient.com",
      "ebuzzing.com",
      "teads.tv",
      "teamblue.services",
      "tellapart.com",
      "telstra.com.au",
      "terra.com.br",
      "tncid.app",
      "hittail.com",
      "medialead.de",
      "adbrain.com",
      "adbrn.com",
      "adsrvr.org",
      "thetradedesk.com",
      "uidapi.com",
      "thismoment.com",
      "convertbox.com",
      "thrtle.com",
      "sensis.com.au",
      "sensisdata.com.au",
      "tiktok.com",
      "tiktokv.us",
      "clmbtech.com",
      "tiqiq.com",
      "tisoomi.com",
      "tlvmedia.com",
      "adoftheyear.com",
      "metrixlab.com",
      "opinionbar.com",
      "clickfuse.com",
      "tonemedia.com",
      "tail.digital",
      "tailtarget.com",
      "tp88trk.com",
      "trackingsoft.com",
      "tradedoubler.com",
      "tradetracker.com",
      "tradetracker.net",
      "traffichaus.com",
      "traffichouse.com",
      "traffic-media.co.uk",
      "trafficscore.com",
      "trafficfactory.biz",
      "trafficfactory.com",
      "tsyndicate.com",
      "trafmag.com",
      "adadvisor.net",
      "aggregateknowledge.com",
      "agkn.com",
      "brighttag.com",
      "btstatic.com",
      "neustar.biz",
      "thebrighttag.com",
      "truoptik.com",
      "traversedlp.com",
      "traveladvertising.com",
      "treasuredata.com",
      "trendemon.com",
      "triggit.com",
      "addme.com",
      "3lift.com",
      "opecloud.com",
      "triplelift.com",
      "tru.am",
      "adlegend.com",
      "trueffect.com",
      "truffle.bid",
      "trustedsite.com",
      "trustpilot.com",
      "trustx.org",
      "trvdp.com",
      "ttarget.ru",
      "programattik.com",
      "buzzlogic.com",
      "twelvefold.com",
      "segment.com",
      "ads-twitter.com",
      "t.co",
      "twyn.com",
      "goldbach.com",
      "goldbachgroup.com",
      "tyroo.com",
      "aralego.com",
      "ucfunnel.com",
      "unanimis.co.uk",
      "unblockia.com",
      "ubembed.com",
      "unbounce.com",
      "udmserve.net",
      "underdogmedia.com",
      "undertone.com",
      "51network.com",
      "wanmo.com",
      "adeurope.com",
      "uimserv.net",
      "supersonicads.com",
      "yellowblue.io",
      "augme.com",
      "hipcricket.com",
      "leadlander.com",
      "localytics.com",
      "trackalyzer.com",
      "upravel.com",
      "upsellit.com",
      "up-value.de",
      "survata.com",
      "utarget.ru",
      "valuead.com",
      "adimg.net",
      "adlantis.jp",
      "buzzparadise.com",
      "amigos.com",
      "getiton.com",
      "medley.com",
      "nostringsattached.com",
      "various.com",
      "admicro.vn",
      "vcmedia.vn",
      "adotube.com",
      "exponential.com",
      "fulltango.com",
      "tribalfusion.com",
      "vdx.tv",
      "veeseo.com",
      "adsvelocity.com",
      "mobclix.com",
      "velti.com",
      "adinplay.com",
      "venatusmedia.com",
      "veoxa.com",
      "veremedia.com",
      "brand.net",
      "maxpointinteractive.com",
      "mxptint.net",
      "versium.com",
      "verticalhealth.net",
      "verticalresponse.com",
      "vresp.com",
      "lkqd.com",
      "lkqd.net",
      "smaato.com",
      "smaato.net",
      "viafoura.co",
      "viafoura.net",
      "adelphic.com",
      "adviva.net",
      "ipredictive.com",
      "sitemeter.com",
      "specificclick.net",
      "specificmedia.co.uk",
      "specificmedia.com",
      "vindicosuite.com",
      "intellitxt.com",
      "picadmedia.com",
      "vibrantmedia.com",
      "vic-m.co",
      "vidazoo.com",
      "videoamp.com",
      "vi.ai",
      "vidoomy.com",
      "viewbix.com",
      "adition.com",
      "yieldlab.de",
      "yieldlab.net",
      "visarity.com",
      "visbrands.com",
      "vizury.com",
      "vocento.com",
      "vocstatic.com",
      "nytrng.com",
      "vrtcal.com",
      "vserv.com",
      "vserv.mobi",
      "vtex.com.br",
      "mediabong.com",
      "contentwidgets.net",
      "wahoha.com",
      "addynamix.com",
      "adchemy.com",
      "adsnative.com",
      "getpolymorph.com",
      "warumbistdusoarm.space",
      "wayfair.com",
      "zedo.com",
      "zincx.com",
      "feedperfect.com",
      "web.com",
      "goutee.top",
      "webmecanik.com",
      "dsmmadvantage.com",
      "weborama.com",
      "weborama.fr",
      "webtraffic.se",
      "wiredminds.com",
      "wiredminds.de",
      "wisepops.com",
      "wisepops.net",
      "wishabi.com",
      "wishabi.net",
      "wistia.com",
      "wordstream.com",
      "wpadmngr.com",
      "accelerator-media.com",
      "decdna.net",
      "decideinteractive.com",
      "gmads.net",
      "groupm.com",
      "kantarmedia.com",
      "mecglobal.com",
      "mindshare.nl",
      "mookie1.com",
      "pm14.com",
      "realmedia.com",
      "themig.com",
      "xaxis.com",
      "adonnetwork.com",
      "primevisibility.com",
      "urtbk.com",
      "bounceexchange.com",
      "bouncex.com",
      "bouncex.net",
      "wknd.ai",
      "wunderkind.co",
      "admanager-xertive.com",
      "xertivemedia.com",
      "webtraxs.com",
      "adplan-ds.com",
      "adinterax.com",
      "adrevolver.com",
      "ads.yahoo.com",
      "adserver.yahoo.com",
      "adsonar.com",
      "adtech.com",
      "adtech.de",
      "adtechjp.com",
      "adtechus.com",
      "advertising.com",
      "advertising.yahoo.com",
      "aolcloud.net",
      "atwola.com",
      "bluelithium.com",
      "brightroll.com",
      "btrll.com",
      "dapper.net",
      "flurry.com",
      "interclick.com",
      "joystiq.com",
      "js7k.com",
      "jumptap.com",
      "marketingsolutions.yahoo.com",
      "mydas.mobi",
      "nexage.com",
      "oath.com",
      "overture.com",
      "patch.com",
      "pictela.com",
      "pictela.net",
      "rightmedia.com",
      "rmxads.com",
      "tacoda.net",
      "vidible.tv",
      "yahoo.net",
      "ybp.yahoo.com",
      "yieldmanager.com",
      "yieldmanager.net",
      "yldmgrimg.net",
      "adfox.ru",
      "adfox.yandex.ru",
      "an.yandex.ru",
      "awaps.yandex.ru",
      "mc.yandex.md",
      "mc.yandex.ru",
      "moikrug.ru",
      "web-visor.com",
      "webvisor.com",
      "webvisor.org",
      "yandex.ru/ads",
      "yandex.ru/clck/click",
      "yandex.ru/clck/counter",
      "yandex.ru/cycounter",
      "yandex.ru/portal/set/any",
      "yandex.ru/set/s/rsya-tag-users/data",
      "clientgear.com",
      "attracto.com",
      "clickhype.com",
      "yellowhammermg.com",
      "yhmg.com",
      "yesads.com",
      "yieldads.com",
      "yldbt.com",
      "yieldbuild.com",
      "yieldify.com",
      "yieldlove-ad-serving.net",
      "yieldmo.com",
      "visx.net",
      "yoc-performance.com",
      "yoc.com",
      "p.yotpo.com",
      "youknowbest.com",
      "affasi.com",
      "gw-ec.com",
      "zaful.com",
      "zaparena.com",
      "zatnoh.com",
      "adpushup.com",
      "zeotap.com",
      "insightgrit.com",
      "boomtrain.com",
      "ignitionone.com",
      "ignitionone.net",
      "rezync.com",
      "rfihub.com",
      "rfihub.net",
      "rocketfuel.com",
      "ru4.com",
      "searchignite.com",
      "xplusone.com",
      "netshelter.com",
      "netshelter.net",
      "clickagy.com",
      "zumobi.com",
      "adobe.com",
      "scene7.com",
      "typekit.com",
      "affirm.com",
      "techlab-cdn.com",
      "amazon.com",
      "cloudfront.net",
      "twitch.tv",
      "anyclip.com",
      "gravatar.com",
      "tumblr.com",
      "wp.com",
      "searchspring.io",
      "bazaarvoice.com",
      "brightcove.com",
      "zencdn.net",
      "browser-update.org",
      "cloudflare.com",
      "cloudflarestream.com",
      "cloudinary.com",
      "org.coveo.com",
      "platform-eu.cloud.coveo.com",
      "platform.cloud.coveo.com",
      "static.cloud.coveo.com",
      "fundraiseup.com",
      "blogger.com",
      "ggpht.com",
      "google.ad",
      "google.ae",
      "google.al",
      "google.am",
      "google.as",
      "google.at",
      "google.az",
      "google.ba",
      "google.be",
      "google.bf",
      "google.bg",
      "google.bi",
      "google.bj",
      "google.bs",
      "google.bt",
      "google.by",
      "google.ca",
      "google.cat",
      "google.cd",
      "google.cf",
      "google.cg",
      "google.ch",
      "google.ci",
      "google.cl",
      "google.cm",
      "google.cn",
      "google.co.ao",
      "google.co.bw",
      "google.co.ck",
      "google.co.cr",
      "google.co.id",
      "google.co.il",
      "google.co.in",
      "google.co.jp",
      "google.co.ke",
      "google.co.kr",
      "google.co.ls",
      "google.co.ma",
      "google.co.mz",
      "google.co.nz",
      "google.co.th",
      "google.co.tz",
      "google.co.ug",
      "google.co.uk",
      "google.co.uz",
      "google.co.ve",
      "google.co.vi",
      "google.co.za",
      "google.co.zm",
      "google.co.zw",
      "google.com",
      "google.com.af",
      "google.com.ag",
      "google.com.ai",
      "google.com.ar",
      "google.com.au",
      "google.com.bd",
      "google.com.bh",
      "google.com.bn",
      "google.com.bo",
      "google.com.br",
      "google.com.bz",
      "google.com.co",
      "google.com.cu",
      "google.com.cy",
      "google.com.do",
      "google.com.ec",
      "google.com.eg",
      "google.com.et",
      "google.com.fj",
      "google.com.gh",
      "google.com.gi",
      "google.com.gt",
      "google.com.hk",
      "google.com.jm",
      "google.com.kh",
      "google.com.kw",
      "google.com.lb",
      "google.com.ly",
      "google.com.mm",
      "google.com.mt",
      "google.com.mx",
      "google.com.my",
      "google.com.na",
      "google.com.nf",
      "google.com.ng",
      "google.com.ni",
      "google.com.np",
      "google.com.om",
      "google.com.pa",
      "google.com.pe",
      "google.com.pg",
      "google.com.ph",
      "google.com.pk",
      "google.com.pr",
      "google.com.py",
      "google.com.qa",
      "google.com.sa",
      "google.com.sb",
      "google.com.sg",
      "google.com.sl",
      "google.com.sv",
      "google.com.tj",
      "google.com.tr",
      "google.com.tw",
      "google.com.ua",
      "google.com.uy",
      "google.com.vc",
      "google.com.vn",
      "google.cv",
      "google.cz",
      "google.de",
      "google.dj",
      "google.dk",
      "google.dm",
      "google.dz",
      "google.ee",
      "google.es",
      "google.fi",
      "google.fm",
      "google.fr",
      "google.ga",
      "google.ge",
      "google.gg",
      "google.gl",
      "google.gm",
      "google.gp",
      "google.gr",
      "google.gy",
      "google.hn",
      "google.hr",
      "google.ht",
      "google.hu",
      "google.ie",
      "google.im",
      "google.iq",
      "google.is",
      "google.it",
      "google.je",
      "google.jo",
      "google.kg",
      "google.ki",
      "google.kz",
      "google.la",
      "google.li",
      "google.lk",
      "google.lt",
      "google.lu",
      "google.lv",
      "google.md",
      "google.me",
      "google.mg",
      "google.mk",
      "google.ml",
      "google.mn",
      "google.ms",
      "google.mu",
      "google.mv",
      "google.mw",
      "google.ne",
      "google.nl",
      "google.no",
      "google.nr",
      "google.nu",
      "google.pl",
      "google.pn",
      "google.ps",
      "google.pt",
      "google.ro",
      "google.rs",
      "google.ru",
      "google.rw",
      "google.sc",
      "google.se",
      "google.sh",
      "google.si",
      "google.sk",
      "google.sm",
      "google.sn",
      "google.so",
      "google.st",
      "google.td",
      "google.tg",
      "google.tk",
      "google.tl",
      "google.tm",
      "google.tn",
      "google.to",
      "google.tt",
      "google.vg",
      "google.vu",
      "google.ws",
      "googleapis.com",
      "googleusercontent.com",
      "googlevideo.com",
      "gstatic.com",
      "recaptcha.net",
      "youtube-nocookie.com",
      "youtube.com",
      "imgix.net",
      "jwpcdn.com",
      "jwplatform.com",
      "jwplayer.com",
      "jwpsrv.com",
      "kaltura.com",
      "yimg.jp",
      "mcafee.com",
      "cdninstagram.com",
      "fbcdn.net",
      "instagram.com",
      "azure.com",
      "azurefd.net",
      "bing.com",
      "dynamics.com",
      "microsoft.com",
      "msn.com",
      "nuance.com",
      "skype.com",
      "typepad.com",
      "oracle.com",
      "paypal.com",
      "pinimg.com",
      "pinterest.com",
      "gigya.com",
      "shop.app",
      "shopifyapps.com",
      "shopifysvc.com",
      "flickr.com",
      "staticflickr.com",
      "stripe.com",
      "trumba.com",
      "uservoice.com",
      "vgwort.de",
      "vimeo.com",
      "vimeocdn.com",
      "my.mail.ru",
      "5min.com",
      "aolcdn.com",
      "winamp.com",
      "yahoo.com",
      "yahooapis.com",
      "yimg.com",
      "kinopoisk.ru",
      "yandex.by",
      "yandex.com",
      "yandex.com.tr",
      "yandex.net",
      "yandex.ru",
      "yandex.st",
      "montwam.top",
      "adsco.re",
      "bluecava.com",
      "pxlclnmdecom-a.akamaihd.net",
      "amocrm.ru",
      "aamapi.com",
      "aamapiv2.com",
      "aamsitecertifier.com",
      "auditedmedia.com",
      "betssonpalantir.com",
      "browseranalytic.com",
      "leadtrackingdata.com",
      "fpnpmcdn.net",
      "openfpcdn.io",
      "answerscloud.com",
      "foresee.com",
      "antifraudjs.friends2follow.com",
      "geetest.com",
      "graphenedigitalanalytics.in",
      "hotelchamp.com",
      "perimeterx.net",
      "px-cloud.net",
      "imedia.cz",
      "hcaptcha.com",
      "islay.tech",
      "itch.io",
      "k-analytix.com",
      "konduto.com",
      "ztsrv.com",
      "maxmind.com",
      "mmapiws.com",
      "mercadopago.com",
      "negishim.org",
      "nofraud.com",
      "notolytix.com",
      "simility.com",
      "pixanalytics.com",
      "perfdrive.com",
      "thirdwatch.ai",
      "rollick.io",
      "sardine.ai",
      "sift.com",
      "siftscience.com",
      "signifyd.com",
      "24smi.net",
      "storeland.ru",
      "techsolutions.com.tw",
      "fraudmetrix.cn",
      "tongdun.net",
      "tracify.ai",
      "friendshipmale.com",
      "unseenreport.com",
      "sf14g.com",
      "xfyun.cn",
      "yoox.it",
      "zipmoney.com.au",
      "avantmetrics.com",
      "doubleverify.com",
      "developers.google.com",
      "gmail.com",
      "google-analytics.com",
      "googlemail.com",
      "plus.google.com",
      "plusone.google.com",
      "postrank.com",
      "smartlock.google.com",
      "voice.google.com",
      "y-track.com",
      "atdmt.com",
      "facebook.com",
      "facebook.net",
      "fb.com",
      "fbsbx.com",
      "friendfeed.com",
      "kissmetrics.com",
      "polen.com.br",
      "flightzy.date",
      "zymerget.bid",
      "authedmine.com",
      "bmst.pw",
      "cashbeet.com",
      "serv1swork.com",
      "coinpot.co",
      "cryptaloot.pro",
      "crypto-loot.com",
      "gitgrub.pro",
      "reauthenticator.com",
      "statdynamic.com",
      "webmine.pro",
      "crypto-webminer.com",
      "ethtrader.de",
      "adless.io",
      "freecontent.date",
      "freecontent.stream",
      "hashing.win",
      "hostingcloud.racing",
      "hostingcloud.science",
      "jsecoin.com",
      "analytics.blue",
      "besstahete.info",
      "feesocrald.com",
      "gramombird.com",
      "istlandoll.com",
      "mepirtedic.com",
      "pampopholf.com",
      "tercabilis.info",
      "tulip18.com",
      "minescripts.info",
      "nerohut.com",
      "service4refresh.info",
      "sparechange.io",
      "swiftmining.win",
      "authedwebmine.cz",
      "webmine.cz",
      "webminepool.com",
      "webmining.co"
    ]
  }
};
const getBlockedHosts = (preferences) => {
  if (!preferences) {
    return Object.values(trackers.categories).flat();
  }
  const blockedHosts = [];
  if (!preferences.Analytics) {
    blockedHosts.push(...trackers.categories.Analytics);
  }
  if (!preferences.Social) {
    blockedHosts.push(...trackers.categories.Social);
  }
  if (!preferences.Advertising) {
    blockedHosts.push(...trackers.categories.Advertising);
  }
  return [...new Set(blockedHosts)];
};
const getBlockedKeywords = (preferences) => {
  if (!preferences) {
    return Object.values(trackers.categories).flat().map((host) => host.replace(/\.[^.]+$/, ""));
  }
  const blockedHosts = getBlockedHosts(preferences);
  return [...new Set(blockedHosts.map((host) => host.replace(/\.[^.]+$/, "")))];
};
const DEFAULT_TRANSLATIONS = {
  title: "",
  message: "This website uses cookies to enhance your experience.",
  buttonText: "Accept",
  declineButtonText: "Decline",
  manageButtonText: "Manage Cookies",
  privacyPolicyText: "Privacy Policy",
  manageTitle: "Cookie Preferences",
  manageMessage: "Manage your cookie preferences below. Essential cookies are always enabled as they are necessary for the website to function properly.",
  manageEssentialTitle: "Essential",
  manageEssentialSubtitle: "Required for the website to function properly",
  manageEssentialStatus: "Status: Always enabled",
  manageEssentialStatusButtonText: "Always On",
  manageAnalyticsTitle: "Analytics",
  manageAnalyticsSubtitle: "Help us understand how visitors interact with our website",
  manageSocialTitle: "Social",
  manageSocialSubtitle: "Enable social media features and sharing",
  manageAdvertTitle: "Advertising",
  manageAdvertSubtitle: "Personalize advertisements and measure their performance",
  manageCookiesStatus: "Status: {{status}} on {{date}}",
  manageCookiesStatusConsented: "Consented",
  manageCookiesStatusDeclined: "Declined",
  manageCancelButtonText: "Cancel",
  manageSaveButtonText: "Save Preferences"
};
function getTranslationValue(tObject, key, params) {
  if (params) {
    return Object.keys(params).reduce((acc, param) => {
      return acc.replace(
        new RegExp(`{{\\s*${param}\\s*}}`, "g"),
        params[param]
      );
    }, tObject[key]);
  }
  return tObject[key];
}
function createTFunction(translations, translationI18NextPrefix) {
  if (typeof translations === "function") {
    return (key, params) => {
      const fullKey = `${translationI18NextPrefix || ""}${key}`;
      let i18nValue = translations(fullKey, params);
      if (i18nValue === fullKey) {
        i18nValue = null;
      }
      return i18nValue || getTranslationValue(DEFAULT_TRANSLATIONS, key, params);
    };
  }
  return (key, params) => {
    return getTranslationValue(
      { ...DEFAULT_TRANSLATIONS, ...translations || {} },
      key,
      params
    );
  };
}
let originalXhrOpen = null;
let originalFetch = null;
const blockTrackingRequests = (blockedHosts) => {
  if (!originalXhrOpen) {
    originalXhrOpen = XMLHttpRequest.prototype.open;
  }
  if (!originalFetch) {
    originalFetch = window.fetch;
  }
  XMLHttpRequest.prototype.open = function(method, url) {
    const urlString = url.toString();
    if (blockedHosts.some((host) => urlString.includes(host))) {
      return;
    }
    return originalXhrOpen.apply(this, arguments);
  };
  window.fetch = function(url, options) {
    const urlString = url.toString();
    if (typeof urlString === "string" && blockedHosts.some((host) => urlString.includes(host))) {
      return Promise.resolve(
        new Response(null, { status: 403, statusText: "Blocked" })
      );
    }
    return originalFetch.apply(this, arguments);
  };
};
const blockTrackingScripts = (trackingKeywords) => {
  document.querySelectorAll("script").forEach((script) => {
    if (script.src && trackingKeywords.some((keyword) => script.src.includes(keyword))) {
      script.remove();
    }
  });
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement && node.tagName === "SCRIPT") {
          const src = node.getAttribute("src");
          if (src && trackingKeywords.some((keyword) => src.includes(keyword))) {
            node.remove();
          }
        }
      });
    });
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
  return observer;
};
const restoreOriginalRequests = () => {
  if (originalXhrOpen) {
    XMLHttpRequest.prototype.open = originalXhrOpen;
  }
  if (originalFetch) {
    window.fetch = originalFetch;
  }
};
const CookieManagerContext = createContext(
  null
);
const createConsentStatus = (consented) => ({
  consented,
  timestamp: (/* @__PURE__ */ new Date()).toISOString()
});
const createDetailedConsent = (consented) => ({
  Analytics: createConsentStatus(consented),
  Social: createConsentStatus(consented),
  Advertising: createConsentStatus(consented)
});
const CookieManager = ({
  children,
  localStorageKey = "cookie-consent",
  translations,
  translationI18NextPrefix,
  onManage,
  disableAutomaticBlocking = false,
  blockedDomains = [],
  expirationDays = 365,
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showManageConsent, setShowManageConsent] = useState(false);
  const tFunction = useMemo(
    () => createTFunction(translations, translationI18NextPrefix),
    [translations, translationI18NextPrefix]
  );
  const [detailedConsent, setDetailedConsent] = useState(() => {
    const storedConsent = localStorage.getItem(localStorageKey);
    if (storedConsent) {
      try {
        const parsedConsent = JSON.parse(
          storedConsent
        );
        const oldestTimestamp = Math.min(
          ...Object.values(parsedConsent).map(
            (status) => new Date(status.timestamp).getTime()
          )
        );
        const expirationTime = oldestTimestamp + expirationDays * 24 * 60 * 60 * 1e3;
        if (Date.now() > expirationTime) {
          localStorage.removeItem(localStorageKey);
          return null;
        }
        return parsedConsent;
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const hasConsent = detailedConsent ? Object.values(detailedConsent).some((status) => status.consented) : null;
  const observerRef = useRef(null);
  useEffect(() => {
    if (detailedConsent === null && !showManageConsent) {
      setIsVisible(true);
    }
    if (!disableAutomaticBlocking) {
      const currentPreferences = detailedConsent ? {
        Analytics: detailedConsent.Analytics.consented,
        Social: detailedConsent.Social.consented,
        Advertising: detailedConsent.Advertising.consented
      } : null;
      const blockedHosts = [
        ...getBlockedHosts(currentPreferences),
        ...blockedDomains
      ];
      const blockedKeywords = [
        ...getBlockedKeywords(currentPreferences),
        ...blockedDomains
      ];
      if (blockedHosts.length > 0) {
        blockTrackingRequests(blockedHosts);
        observerRef.current = blockTrackingScripts(blockedKeywords);
      } else {
        restoreOriginalRequests();
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
      }
    } else {
      restoreOriginalRequests();
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    }
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [detailedConsent, disableAutomaticBlocking, blockedDomains]);
  const showConsentBanner = () => {
    if (!showManageConsent) {
      setIsVisible(true);
    }
  };
  const acceptCookies = () => {
    const newConsent = createDetailedConsent(true);
    localStorage.setItem(localStorageKey, JSON.stringify(newConsent));
    setDetailedConsent(newConsent);
    setIsVisible(false);
  };
  const declineCookies = () => {
    const newConsent = createDetailedConsent(false);
    localStorage.setItem(localStorageKey, JSON.stringify(newConsent));
    setDetailedConsent(newConsent);
    setIsVisible(false);
  };
  const updateDetailedConsent = (preferences) => {
    const timestamp = (/* @__PURE__ */ new Date()).toISOString();
    const newConsent = {
      Analytics: { consented: preferences.Analytics, timestamp },
      Social: { consented: preferences.Social, timestamp },
      Advertising: { consented: preferences.Advertising, timestamp }
    };
    localStorage.setItem(localStorageKey, JSON.stringify(newConsent));
    setDetailedConsent(newConsent);
    setShowManageConsent(false);
    if (onManage) {
      onManage(preferences);
    }
  };
  const handleManage = () => {
    setIsVisible(false);
    setShowManageConsent(true);
  };
  const handleCancelManage = () => {
    setShowManageConsent(false);
    setIsVisible(true);
  };
  const value = {
    hasConsent,
    isDeclined: hasConsent === false,
    detailedConsent,
    showConsentBanner,
    acceptCookies,
    declineCookies,
    updateDetailedConsent
  };
  return /* @__PURE__ */ jsxs(CookieManagerContext.Provider, { value, children: [
    children,
    isVisible && /* @__PURE__ */ jsx(
      CookieConsenter,
      {
        ...props,
        tFunction,
        localStorageKey,
        onAccept: acceptCookies,
        onDecline: declineCookies,
        onManage: handleManage,
        detailedConsent,
        initialPreferences: detailedConsent ? {
          Analytics: detailedConsent.Analytics.consented,
          Social: detailedConsent.Social.consented,
          Advertising: detailedConsent.Advertising.consented
        } : void 0
      }
    ),
    showManageConsent && /* @__PURE__ */ jsx("div", { className: "cookie-manager", children: /* @__PURE__ */ jsx("div", { className: "fixed inset-0 z-[99999] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4", children: /* @__PURE__ */ jsx(
      "div",
      {
        className: `w-full max-w-lg rounded-xl p-6 ${props.theme === "light" ? "bg-white/95 ring-1 ring-black/10" : "bg-black/95 ring-1 ring-white/10"}`,
        children: /* @__PURE__ */ jsx(
          ManageConsent,
          {
            tFunction,
            theme: props.theme,
            onSave: updateDetailedConsent,
            onCancel: handleCancelManage,
            initialPreferences: detailedConsent ? {
              Analytics: detailedConsent.Analytics.consented,
              Social: detailedConsent.Social.consented,
              Advertising: detailedConsent.Advertising.consented
            } : void 0,
            detailedConsent
          }
        )
      }
    ) }) })
  ] });
};
const useCookieConsent = () => {
  const context = useContext(CookieManagerContext);
  if (!context) {
    throw new Error("useCookieConsent must be used within a CookieManager");
  }
  return context;
};
export {
  CookieConsenter,
  CookieManager,
  useCookieConsent
};
